import db from '../db/index';
import syncService from '../services/syncService';
import { handleUnauthorizedResponse } from '../services/authSession';

const API_BASE_URL = '/api';
const SETTINGS_KEY = 'gaza_pharmacy_settings';

function getAuthHeaders(extraHeaders = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...extraHeaders,
  };
  const token = localStorage.getItem('gaza_pharmacy_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

function getSettings() {
  try {
    return JSON.parse(localStorage.getItem(SETTINGS_KEY)) || {};
  } catch {
    return {};
  }
}

function getCurrentAuthUser() {
  try {
    return JSON.parse(localStorage.getItem('gaza_pharmacy_user')) || null;
  } catch {
    return null;
  }
}

function saveSettings(data) {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(data));
}

function notifyAlertRefresh(resource) {
  if (typeof window !== 'undefined' && ['pos', 'purchases', 'damages', 'batches', 'medicines'].includes(resource)) {
    window.dispatchEvent(new Event('pharmacy-notifications-changed'));
  }
}

function extractId(endpoint) {
  const clean = endpoint.split('?')[0];
  const parts = clean.split('/').filter(Boolean);
  for (const p of parts) {
    if (/^\d+$/.test(p)) return parseInt(p);
  }
  return null;
}

function parseEndpoint(endpoint) {
  const clean = endpoint.split('?')[0];
  const parts = clean.split('/').filter(Boolean);
  return { parts, id: extractId(endpoint) };
}

function parseQueryParams(endpoint) {
  const qIndex = endpoint.indexOf('?');
  if (qIndex === -1) return {};
  const params = {};
  const searchParams = new URLSearchParams(endpoint.slice(qIndex));
  for (const [k, v] of searchParams.entries()) {
    params[k] = v;
  }
  return params;
}

async function applyDateFilter(collection, params) {
  let items = await collection.toArray();
  if (params.date_from) {
    items = items.filter((i) => {
      const d = i.created_at || i.date || i.purchase_date || i.refund_date || '';
      return d >= params.date_from;
    });
  }
  if (params.date_to) {
    items = items.filter((i) => {
      const d = i.created_at || i.date || i.purchase_date || i.refund_date || '';
      return d <= params.date_to;
    });
  }
  return items;
}

async function enrichMedicine(med) {
  const batches = await db.batches.where('medicine_id').equals(med.id).toArray();
  const sortedBatches = batches
    .filter((b) => (b.quantity || 0) > 0)
    .sort((a, b) => {
      if (!a.expiry_date) return 1;
      if (!b.expiry_date) return -1;
      return new Date(a.expiry_date) - new Date(b.expiry_date);
    });
  const total_stock = batches.reduce((sum, b) => sum + (b.quantity || 0), 0);
  const nearest_expiry = sortedBatches.length > 0 ? sortedBatches[0].expiry_date : (med.nearest_expiry || null);
  const costPrices = batches.filter((b) => b.cost_price > 0).map((b) => b.cost_price);
  const avg_cost_price = costPrices.length > 0 ? costPrices.reduce((a, b) => a + b, 0) / costPrices.length : (med.avg_cost_price || 0);
  return { ...med, total_stock, nearest_expiry, avg_cost_price, batches: sortedBatches };
}

async function computeDashboard() {
  const medicines = await db.medicines.toArray();
  const enriched = await Promise.all(medicines.map(enrichMedicine));
  const sales = await db.sales.toArray();
  const purchases = await db.purchases.toArray();
  const expenses = (await db.expenses.toArray()).filter((e) => e.category !== 'Advance Payment');
  const customers = await db.customers.toArray();
  const suppliers = await db.suppliers.toArray();

  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 7);

  const todaySales = sales.filter((s) => (s.created_at || '').startsWith(today));
  const monthSales = sales.filter((s) => (s.created_at || '').startsWith(monthStart));

  const todayOrders = todaySales.length;
  const todayTotal = todaySales.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0);
  const monthOrders = monthSales.length;
  const monthTotal = monthSales.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0);

  const stockValue = enriched.reduce((sum, m) => sum + (m.total_stock * (m.avg_cost_price || 0)), 0);
  const stockItems = enriched.reduce((sum, m) => sum + m.total_stock, 0);

  const receivableFromBalances = customers
    .reduce((sum, c) => sum + (Number(c.balance || 0) > 0 ? Number(c.balance) : 0), 0);
  const payableFromBalances = suppliers
    .reduce((sum, s) => sum + (Number(s.balance || 0) > 0 ? Number(s.balance) : 0), 0);
  const unlinkedReceivable = sales.filter((s) => !s.customer_id && s.payment_status !== 'paid').reduce((sum, s) => sum + Math.max(0, Number(s.total || 0) - Number(s.paid_amount || 0)), 0);
  const unlinkedPayable = purchases.filter((p) => !p.supplier_id && p.payment_status !== 'paid').reduce((sum, p) => sum + Math.max(0, Number(p.total || 0) - Number(p.paid_amount || 0)), 0);
  const receivable = receivableFromBalances + unlinkedReceivable;
  const payable = payableFromBalances + unlinkedPayable;
  const customerAdvance = customers
    .reduce((sum, c) => sum + Math.max(0, -Number(c.balance || 0)), 0);
  const supplierAdvance = suppliers
    .reduce((sum, s) => sum + Math.max(0, Number(s.credit_balance || 0)), 0);

  const lowStockItems = enriched.filter((m) => {
    if (m.total_stock <= 0 || m.total_stock > (m.reorder_level || 0)) return false;
    const isExpired = m.nearest_expiry && new Date(m.nearest_expiry) < new Date(today);
    return !isExpired;
  });
  const expiryItems = enriched.filter((m) => {
    if (!m.nearest_expiry || m.total_stock <= 0) return false;
    const days = Math.ceil((new Date(m.nearest_expiry) - new Date()) / (1000 * 60 * 60 * 24));
    return days <= 90;
  });

  const trend = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().slice(0, 10);
    const dayTotal = sales
      .filter((s) => (s.created_at || '').startsWith(dateStr))
      .reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0);
    trend.push({ date: d.toLocaleDateString('en', { weekday: 'short' }), total: dayTotal });
  }

  const sellerMap = {};
  for (const s of sales) {
    const items = await db.sale_items.where('sale_id').equals(s.id).toArray();
    for (const item of items) {
      const med = medicines.find((m) => m.id === item.medicine_id);
      const name = med?.name || 'Unknown';
      if (!sellerMap[name]) sellerMap[name] = { name, qty: 0, revenue: 0 };
      sellerMap[name].qty += item.quantity || 0;
      sellerMap[name].revenue += (item.quantity || 0) * (item.unit_price || 0);
    }
  }
  const topSellers = Object.values(sellerMap).sort((a, b) => b.revenue - a.revenue).slice(0, 5);

  const expiryAlerts = expiryItems.map((m) => ({
    name: m.name,
    batch_number: m.batches?.[0]?.batch_number || '-',
    quantity: m.batches?.[0]?.quantity || 0,
    days_left: Math.ceil((new Date(m.nearest_expiry) - new Date()) / (1000 * 60 * 60 * 24)),
  }));

  const lowStock = lowStockItems.map((m) => ({
    name: m.name,
    reorder_level: m.reorder_level,
    stock: m.total_stock,
  }));

  const customerPayments = db.customer_payments ? await db.customer_payments.toArray() : [];
  const supplierPayments = db.supplier_payments ? await db.supplier_payments.toArray() : [];
  const supplierRefunds = db.supplier_refunds ? await db.supplier_refunds.toArray() : [];
  const customerRefunds = db.customer_refunds ? await db.customer_refunds.toArray() : [];
  const staff = db.staff ? await db.staff.toArray() : [];
  const cashTransactions = db.cash_transactions ? await db.cash_transactions.toArray() : [];
  const staffTransactions = db.staff_transactions ? await db.staff_transactions.toArray() : [];

  // Exclude expenses already accounted for as staff salary payouts
  const staffSalaryExpenseIds = new Set(staffTransactions.filter((st) => st.expense_id && st.type === 'salary').map((st) => Number(st.expense_id)));

  const isCashOrCredit = (method) => ['cash', 'credit'].includes(String(method || 'cash').toLowerCase());

  // Cash Calculations
  const salesCashIn = sales
    .filter((s) => (Number(s.checkout_paid_amount ?? s.paid_amount) > 0) && isCashOrCredit(s.payment_method))
    .reduce((sum, s) => sum + Math.min(Number(s.checkout_paid_amount ?? s.paid_amount) || 0, Number(s.total) || 0), 0);

  const cpIn = customerPayments
    .filter((p) => Number(p.amount) > 0 && String(p.payment_method || 'cash').toLowerCase() === 'cash' && (p.payment_type === 'received' || !p.payment_type))
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const srIn = supplierRefunds
    .filter((r) => Number(r.refund_amount) > 0)
    .reduce((sum, r) => sum + (Number(r.refund_amount) || 0), 0);

  const spRefundIn = supplierPayments
    .filter((p) => Number(p.amount) > 0 && String(p.payment_method || 'cash').toLowerCase() === 'cash' && p.payment_type === 'refund_received')
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const ctDepositIn = cashTransactions
    .filter((ct) => Number(ct.amount) > 0 && ct.type === 'deposit' && String(ct.payment_method || 'cash').toLowerCase() === 'cash')
    .reduce((sum, ct) => sum + (Number(ct.amount) || 0), 0);

  const totalCashIn = salesCashIn + cpIn + srIn + spRefundIn + ctDepositIn;

  const expOut = expenses
    .filter((e) => Number(e.amount) > 0 && String(e.payment_method || 'cash').toLowerCase() === 'cash' && !staffSalaryExpenseIds.has(Number(e.id)))
    .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);

  const staffSalaryOut = staffTransactions
    .filter((st) => st.type === 'salary' && Number(st.net_paid || st.amount) > 0 && String(st.payment_method || 'cash').toLowerCase() === 'cash')
    .reduce((sum, st) => sum + (Number(st.net_paid || st.amount) || 0), 0);

  const staffAdvanceOut = staffTransactions
    .filter((st) => st.type === 'advance' && Number(st.amount) > 0 && String(st.payment_method || 'cash').toLowerCase() === 'cash')
    .reduce((sum, st) => sum + (Number(st.amount) || 0), 0);

  const purOut = purchases
    .filter((p) => Number(p.checkout_paid_amount ?? p.paid_amount) > 0 && isCashOrCredit(p.payment_method))
    .reduce((sum, p) => sum + Math.min(Number(p.checkout_paid_amount ?? p.paid_amount) || 0, Number(p.total) || 0), 0);

  const spOut = supplierPayments
    .filter((p) => Number(p.amount) > 0 && String(p.payment_method || 'cash').toLowerCase() === 'cash' && p.payment_type !== 'refund_received')
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const crOut = customerRefunds
    .filter((r) => Number(r.refund_amount) > 0)
    .reduce((sum, r) => sum + (Number(r.refund_amount) || 0), 0);

  const cpRefundOut = customerPayments
    .filter((p) => Number(p.amount) > 0 && String(p.payment_method || 'cash').toLowerCase() === 'cash' && p.payment_type === 'refund')
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const ctWithdrawOut = cashTransactions
    .filter((ct) => Number(ct.amount) > 0 && ct.type === 'withdraw' && String(ct.payment_method || 'cash').toLowerCase() === 'cash')
    .reduce((sum, ct) => sum + (Number(ct.amount) || 0), 0);

  const totalCashOut = expOut + staffSalaryOut + staffAdvanceOut + purOut + spOut + crOut + cpRefundOut + ctWithdrawOut;
  const totalBalance = totalCashIn - totalCashOut;

  const isDigitalMethod = (method) => ['card', 'online', 'bank'].includes(String(method || '').toLowerCase());
  const digitalCtDepositIn = cashTransactions
    .filter((ct) => Number(ct.amount) > 0 && ct.type === 'deposit' && isDigitalMethod(ct.payment_method))
    .reduce((sum, ct) => sum + (Number(ct.amount) || 0), 0);
  const digitalCtWithdrawOut = cashTransactions
    .filter((ct) => Number(ct.amount) > 0 && ct.type === 'withdraw' && isDigitalMethod(ct.payment_method))
    .reduce((sum, ct) => sum + (Number(ct.amount) || 0), 0);

  const digitalIn = sales
    .filter((s) => Number(s.checkout_paid_amount ?? s.paid_amount) > 0 && isDigitalMethod(s.payment_method))
    .reduce((sum, s) => sum + Math.min(Number(s.checkout_paid_amount ?? s.paid_amount) || 0, Number(s.total) || 0), 0)
    + customerPayments.filter((p) => Number(p.amount) > 0 && isDigitalMethod(p.payment_method) && (p.payment_type === 'received' || !p.payment_type)).reduce((sum, p) => sum + Number(p.amount || 0), 0)
    + supplierPayments.filter((p) => Number(p.amount) > 0 && isDigitalMethod(p.payment_method) && p.payment_type === 'refund_received').reduce((sum, p) => sum + Number(p.amount || 0), 0)
    + digitalCtDepositIn;
  const digitalOut = expenses.filter((e) => Number(e.amount) > 0 && isDigitalMethod(e.payment_method)).reduce((sum, e) => sum + Number(e.amount || 0), 0)
    + purchases.filter((p) => Number(p.checkout_paid_amount ?? p.paid_amount) > 0 && isDigitalMethod(p.payment_method)).reduce((sum, p) => sum + Math.min(Number(p.checkout_paid_amount ?? p.paid_amount) || 0, Number(p.total) || 0), 0)
    + supplierPayments.filter((p) => Number(p.amount) > 0 && isDigitalMethod(p.payment_method) && p.payment_type !== 'refund_received').reduce((sum, p) => sum + Number(p.amount || 0), 0)
    + customerPayments.filter((p) => Number(p.amount) > 0 && isDigitalMethod(p.payment_method) && p.payment_type === 'refund').reduce((sum, p) => sum + Number(p.amount || 0), 0)
    + digitalCtWithdrawOut;

  // Today Cash In / Out
  const todaySalesCashIn = todaySales
    .filter((s) => Number(s.checkout_paid_amount ?? s.paid_amount) > 0 && isCashOrCredit(s.payment_method))
    .reduce((sum, s) => sum + Math.min(Number(s.checkout_paid_amount ?? s.paid_amount) || 0, Number(s.total) || 0), 0);

  const todayCpIn = customerPayments
    .filter((p) => ((p.payment_date || '').startsWith(today) || (p.created_at || '').startsWith(today)) && Number(p.amount) > 0 && String(p.payment_method || 'cash').toLowerCase() === 'cash' && (p.payment_type === 'received' || !p.payment_type))
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const todaySrIn = supplierRefunds
    .filter((r) => ((r.refund_date || '').startsWith(today) || (r.created_at || '').startsWith(today)) && Number(r.refund_amount) > 0)
    .reduce((sum, r) => sum + (Number(r.refund_amount) || 0), 0);

  const todaySpRefundIn = supplierPayments
    .filter((p) => ((p.payment_date || '').startsWith(today) || (p.created_at || '').startsWith(today)) && Number(p.amount) > 0 && String(p.payment_method || 'cash').toLowerCase() === 'cash' && p.payment_type === 'refund_received')
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const todayCtDepositIn = cashTransactions
    .filter((ct) => ((ct.transaction_date || '').startsWith(today) || (ct.created_at || '').startsWith(today)) && Number(ct.amount) > 0 && ct.type === 'deposit' && String(ct.payment_method || 'cash').toLowerCase() === 'cash')
    .reduce((sum, ct) => sum + (Number(ct.amount) || 0), 0);

  const todayCashIn = todaySalesCashIn + todayCpIn + todaySrIn + todaySpRefundIn + todayCtDepositIn;

  const todayExpOut = expenses
    .filter((e) => ((e.date || '').startsWith(today) || (e.created_at || '').startsWith(today)) && Number(e.amount) > 0 && String(e.payment_method || 'cash').toLowerCase() === 'cash' && !staffSalaryExpenseIds.has(Number(e.id)))
    .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);

  const todayStaffSalaryOut = staffTransactions
    .filter((st) => ((st.transaction_date || '').startsWith(today) || (st.created_at || '').startsWith(today)) && st.type === 'salary' && Number(st.net_paid || st.amount) > 0 && String(st.payment_method || 'cash').toLowerCase() === 'cash')
    .reduce((sum, st) => sum + (Number(st.net_paid || st.amount) || 0), 0);

  const todayStaffAdvanceOut = staffTransactions
    .filter((st) => ((st.transaction_date || '').startsWith(today) || (st.created_at || '').startsWith(today)) && st.type === 'advance' && Number(st.amount) > 0 && String(st.payment_method || 'cash').toLowerCase() === 'cash')
    .reduce((sum, st) => sum + (Number(st.amount) || 0), 0);

  const todayPurOut = purchases
    .filter((p) => ((p.purchase_date || '').startsWith(today) || (p.created_at || '').startsWith(today)) && Number(p.checkout_paid_amount ?? p.paid_amount) > 0 && isCashOrCredit(p.payment_method))
    .reduce((sum, p) => sum + Math.min(Number(p.checkout_paid_amount ?? p.paid_amount) || 0, Number(p.total) || 0), 0);

  const todaySpOut = supplierPayments
    .filter((p) => ((p.payment_date || '').startsWith(today) || (p.created_at || '').startsWith(today)) && Number(p.amount) > 0 && String(p.payment_method || 'cash').toLowerCase() === 'cash' && p.payment_type !== 'refund_received')
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const todayCrOut = customerRefunds
    .filter((r) => ((r.refund_date || '').startsWith(today) || (r.created_at || '').startsWith(today)) && Number(r.refund_amount) > 0)
    .reduce((sum, r) => sum + (Number(r.refund_amount) || 0), 0);

  const todayCpRefundOut = customerPayments
    .filter((p) => ((p.payment_date || '').startsWith(today) || (p.created_at || '').startsWith(today)) && Number(p.amount) > 0 && String(p.payment_method || 'cash').toLowerCase() === 'cash' && p.payment_type === 'refund')
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const todayCtWithdrawOut = cashTransactions
    .filter((ct) => ((ct.transaction_date || '').startsWith(today) || (ct.created_at || '').startsWith(today)) && Number(ct.amount) > 0 && ct.type === 'withdraw' && String(ct.payment_method || 'cash').toLowerCase() === 'cash')
    .reduce((sum, ct) => sum + (Number(ct.amount) || 0), 0);

  const todayCashOut = todayExpOut + todayStaffSalaryOut + todayStaffAdvanceOut + todayPurOut + todaySpOut + todayCrOut + todayCpRefundOut + todayCtWithdrawOut;

  const todayCtDigitalDepositIn = cashTransactions
    .filter((ct) => ((ct.transaction_date || '').startsWith(today) || (ct.created_at || '').startsWith(today)) && Number(ct.amount) > 0 && ct.type === 'deposit' && isDigitalMethod(ct.payment_method))
    .reduce((sum, ct) => sum + (Number(ct.amount) || 0), 0);

  const todayCtDigitalWithdrawOut = cashTransactions
    .filter((ct) => ((ct.transaction_date || '').startsWith(today) || (ct.created_at || '').startsWith(today)) && Number(ct.amount) > 0 && ct.type === 'withdraw' && isDigitalMethod(ct.payment_method))
    .reduce((sum, ct) => sum + (Number(ct.amount) || 0), 0);

  const todayDigitalIn = todaySales.filter((s) => Number(s.checkout_paid_amount ?? s.paid_amount) > 0 && isDigitalMethod(s.payment_method)).reduce((sum, s) => sum + Math.min(Number(s.checkout_paid_amount ?? s.paid_amount) || 0, Number(s.total) || 0), 0)
    + customerPayments.filter((p) => ((p.payment_date || '').startsWith(today) || (p.created_at || '').startsWith(today)) && Number(p.amount) > 0 && isDigitalMethod(p.payment_method) && (p.payment_type === 'received' || !p.payment_type)).reduce((sum, p) => sum + Number(p.amount || 0), 0)
    + todayCtDigitalDepositIn;
  const todayDigitalOut = expenses.filter((e) => ((e.date || '').startsWith(today) || (e.created_at || '').startsWith(today)) && Number(e.amount) > 0 && isDigitalMethod(e.payment_method)).reduce((sum, e) => sum + Number(e.amount || 0), 0)
    + purchases.filter((p) => ((p.purchase_date || '').startsWith(today) || (p.created_at || '').startsWith(today)) && Number(p.checkout_paid_amount ?? p.paid_amount) > 0 && isDigitalMethod(p.payment_method)).reduce((sum, p) => sum + Math.min(Number(p.checkout_paid_amount ?? p.paid_amount) || 0, Number(p.total) || 0), 0)
    + todayCtDigitalWithdrawOut;

  // Expenses summary
  const todayExpenses = expenses.filter((e) => (e.date || '').startsWith(today) || (e.created_at || '').startsWith(today));
  const monthExpenses = expenses.filter((e) => (e.date || '').startsWith(monthStart) || (e.created_at || '').startsWith(monthStart));

  // Today purchases
  const todayPurchasesList = purchases.filter((p) => (p.purchase_date || '').startsWith(today) || (p.created_at || '').startsWith(today));
  const monthPurchasesList = purchases.filter((p) => (p.purchase_date || '').startsWith(monthStart) || (p.created_at || '').startsWith(monthStart));

  return {
    todaySales: {
      orders: todayOrders,
      total: todayTotal,
      cash: todaySalesCashIn,
      card: todaySales.filter((s) => String(s.payment_method).toLowerCase() === 'card').reduce((sum, s) => sum + (parseFloat(s.checkout_paid_amount ?? s.paid_amount) || 0), 0),
      online: todaySales.filter((s) => String(s.payment_method).toLowerCase() === 'online').reduce((sum, s) => sum + (parseFloat(s.checkout_paid_amount ?? s.paid_amount) || 0), 0),
      credit: todaySales.reduce((sum, s) => sum + Math.max(0, (parseFloat(s.total) || 0) - (parseFloat(s.paid_amount) || 0)), 0),
    },
    monthSales: { orders: monthOrders, total: monthTotal },
    todayPurchases: {
      todayOrders: todayPurchasesList.length,
      todayTotal: todayPurchasesList.reduce((sum, p) => sum + (parseFloat(p.total) || 0), 0),
      todayPaid: todayPurchasesList.reduce((sum, p) => sum + (parseFloat(p.paid_amount) || 0), 0),
      monthOrders: monthPurchasesList.length,
      monthTotal: monthPurchasesList.reduce((sum, p) => sum + (parseFloat(p.total) || 0), 0),
      monthPaid: monthPurchasesList.reduce((sum, p) => sum + (parseFloat(p.paid_amount) || 0), 0),
    },
    stockValue: { value: stockValue, items: stockItems },
    receivable: { total: receivable, fromSales: unlinkedReceivable, fromBalances: receivableFromBalances },
    payable: { total: payable, fromPurchases: unlinkedPayable, fromBalances: payableFromBalances },
    alerts: { lowStock: lowStockItems.length, expiry: expiryItems.length },
    remainingCash: {
      totalBalance,
      todayCashIn,
      todayCashOut,
      todayNet: todayCashIn - todayCashOut,
      details: {
        salesCash: salesCashIn,
        customerPayments: cpIn,
        supplierRefunds: srIn,
        supplierAdvanceRefunds: spRefundIn,
        cashDeposits: ctDepositIn,
        expenses: expOut,
        staffAdvances: staffAdvanceOut,
        staffSalaries: staffSalaryOut,
        purchasesCash: purOut,
        supplierPayments: spOut,
        customerRefunds: crOut,
        customerAdvanceRefunds: cpRefundOut,
        cashWithdrawals: ctWithdrawOut,
      },
    },
    digitalBalance: {
      totalBalance: digitalIn - digitalOut,
      totalIn: digitalIn,
      totalOut: digitalOut,
      todayIn: todayDigitalIn,
      todayOut: todayDigitalOut,
    },
    expenses: {
      todayTotal: todayExpenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0),
      todayCount: todayExpenses.length,
      monthTotal: monthExpenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0),
      monthCount: monthExpenses.length,
    },
    staffPaid: {
      todayTotal: 0,
      todaySalary: 0,
      todayAdvance: 0,
      todayCount: 0,
      monthTotal: 0,
      monthSalary: 0,
      monthAdvance: 0,
      monthCount: 0,
    },
    udhaarReceived: {
      // Customer advance created during registration is not udhaar recovery.
      todayTotal: customerPayments.filter((p) => ((p.payment_date || '').startsWith(today) || (p.created_at || '').startsWith(today)) && !String(p.notes || '').startsWith('Opening advance received')).reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0),
      todayCount: customerPayments.filter((p) => ((p.payment_date || '').startsWith(today) || (p.created_at || '').startsWith(today)) && !String(p.notes || '').startsWith('Opening advance received')).length,
      monthTotal: customerPayments.filter((p) => ((p.payment_date || '').startsWith(monthStart) || (p.created_at || '').startsWith(monthStart)) && !String(p.notes || '').startsWith('Opening advance received')).reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0),
      monthCount: customerPayments.filter((p) => ((p.payment_date || '').startsWith(monthStart) || (p.created_at || '').startsWith(monthStart)) && !String(p.notes || '').startsWith('Opening advance received')).length,
      allTimeTotal: customerPayments.filter((p) => !String(p.notes || '').startsWith('Opening advance received')).reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0),
      allTimeCount: customerPayments.filter((p) => !String(p.notes || '').startsWith('Opening advance received')).length,
    },
    udhaarGiven: {
      todayTotal: todaySales.reduce((sum, s) => sum + Math.max(0, (parseFloat(s.total) || 0) - (parseFloat(s.paid_amount) || 0)), 0),
      todayCount: todaySales.filter((s) => String(s.payment_method).toLowerCase() === 'credit' || Number(s.total || 0) > Number(s.paid_amount || 0)).length,
      monthTotal: monthSales.reduce((sum, s) => sum + Math.max(0, (parseFloat(s.total) || 0) - (parseFloat(s.paid_amount) || 0)), 0),
      monthCount: monthSales.filter((s) => String(s.payment_method).toLowerCase() === 'credit' || Number(s.total || 0) > Number(s.paid_amount || 0)).length,
    },
    advances: {
      customerReceived: customerAdvance,
      customerCount: customers.filter((c) => Number(c.balance || 0) < 0).length,
      supplierPaid: supplierAdvance,
      supplierCount: suppliers.filter((s) => Number(s.credit_balance || 0) > 0).length,
    },
    revenueTrend: trend,
    topSellers,
    expiryAlerts,
    lowStock,
  };
}

async function computeAccounting(params = {}) {
  const today = new Date().toISOString().slice(0, 10);
  const dateFrom = params.date_from || '';
  const dateTo = params.date_to || '';
  const method = String(params.method || 'all').toLowerCase();
  const page = Math.max(1, parseInt(params.page, 10) || 1);
  const limit = Math.max(1, Math.min(200, parseInt(params.limit, 10) || 20));
  const offset = (page - 1) * limit;

  const entries = [];

  // 1. Sales
  const sales = (await db.sales.toArray()).filter((s) => Number(s.paid_amount) > 0);
  const customers = await db.customers.toArray();
  const customerMap = {};
  customers.forEach((c) => { customerMap[c.id] = c.name; });

  for (const s of sales) {
    const sMethod = String(s.payment_method || 'cash').toLowerCase();
    entries.push({
      id: s.id,
      type: 'sale',
      reference: s.invoice_number || `INV-${s.id}`,
      description: `Sale: ${customerMap[s.customer_id] || 'Walk-in Customer'}`,
      payment_method: sMethod === 'credit' && Number(s.paid_amount) > 0 ? 'cash' : sMethod,
      debit: Math.min(Number(s.paid_amount) || 0, Number(s.total) || (Number(s.paid_amount) || 0)),
      credit: 0,
      transaction_time: s.created_at || new Date().toISOString(),
      transaction_date: (s.created_at || '').slice(0, 10),
    });
  }

  // 2. Customer Payments
  if (db.customer_payments) {
    const cp = (await db.customer_payments.toArray()).filter((p) => Number(p.amount) > 0);
    for (const p of cp) {
      const isRefund = p.payment_type === 'refund';
      entries.push({
        id: p.id,
        type: 'customer_payment',
        reference: isRefund ? `CP-REF-${String(p.id).padStart(4, '0')}` : `CP-${String(p.id).padStart(4, '0')}`,
        description: isRefund
          ? `Customer Advance Refund Paid: ${customerMap[p.customer_id] || 'Customer'}${p.notes ? ` (${p.notes})` : ''}`
          : `Customer Payment: ${customerMap[p.customer_id] || 'Customer'}${p.notes ? ` (${p.notes})` : ''}`,
        payment_method: String(p.payment_method || 'cash').toLowerCase(),
        debit: isRefund ? 0 : (Number(p.amount) || 0),
        credit: isRefund ? (Number(p.amount) || 0) : 0,
        transaction_time: p.created_at || p.payment_date || new Date().toISOString(),
        transaction_date: (p.payment_date || p.created_at || '').slice(0, 10),
      });
    }
  }

  // 3. Supplier Refunds
  if (db.supplier_refunds) {
    const suppliers = await db.suppliers.toArray();
    const supMap = {};
    suppliers.forEach((sup) => { supMap[sup.id] = sup.name; });
    const sRefunds = (await db.supplier_refunds.toArray()).filter((r) => Number(r.refund_amount) > 0);
    for (const r of sRefunds) {
      entries.push({
        id: r.id,
        type: 'supplier_refund',
        reference: `REF-S-${String(r.id).padStart(4, '0')}`,
        description: `Supplier Refund: ${supMap[r.supplier_id] || 'Supplier'}`,
        payment_method: 'cash',
        debit: Number(r.refund_amount) || 0,
        credit: 0,
        transaction_time: r.created_at || r.refund_date || new Date().toISOString(),
        transaction_date: (r.refund_date || r.created_at || '').slice(0, 10),
      });
    }
  }

  // Staff transactions
  const staffTransactions = db.staff_transactions ? await db.staff_transactions.toArray() : [];
  const staffSalaryExpenseIds = new Set(staffTransactions.filter((st) => st.expense_id && st.type === 'salary').map((st) => Number(st.expense_id)));

  // 4. Expenses (excluding advance payments and staff salary duplicates)
  const expenses = (await db.expenses.toArray()).filter((e) => Number(e.amount) > 0 && e.category !== 'Advance Payment' && !staffSalaryExpenseIds.has(Number(e.id)));
  for (const e of expenses) {
    entries.push({
      id: e.id,
      type: 'expense',
      reference: `EXP-${String(e.id).padStart(4, '0')}`,
      description: `Expense: ${e.category || 'General'}${e.description ? ` - ${e.description}` : ''}`,
      payment_method: String(e.payment_method || 'cash').toLowerCase(),
      debit: 0,
      credit: Number(e.amount) || 0,
      transaction_time: e.created_at || e.date || new Date().toISOString(),
      transaction_date: (e.date || e.created_at || '').slice(0, 10),
    });
  }

  // 5. Staff Transactions (Salaries & Advances)
  if (db.staff_transactions) {
    const staffList = db.staff ? await db.staff.toArray() : [];
    const staffNameMap = {};
    staffList.forEach((st) => { staffNameMap[st.id] = st.name; });
    for (const st of staffTransactions) {
      if (st.type === 'salary' || st.type === 'advance') {
        const isSalary = st.type === 'salary';
        const amt = isSalary ? (Number(st.net_paid) || Number(st.amount) || 0) : (Number(st.amount) || 0);
        if (amt > 0) {
          entries.push({
            id: st.id,
            type: isSalary ? 'staff_salary' : 'staff_advance',
            reference: `ST-${String(st.id).padStart(4, '0')}`,
            description: `${isSalary ? 'Salary Paid: ' : 'Staff Advance: '}${staffNameMap[st.staff_id] || 'Staff'}${st.notes ? ` (${st.notes})` : ''}`,
            payment_method: String(st.payment_method || 'cash').toLowerCase(),
            debit: 0,
            credit: amt,
            transaction_time: st.created_at || st.transaction_date || new Date().toISOString(),
            transaction_date: (st.transaction_date || st.created_at || '').slice(0, 10),
          });
        }
      }
    }
  }

  // 6. Purchases
  if (db.purchases) {
    const suppliers = await db.suppliers.toArray();
    const supMap = {};
    suppliers.forEach((sup) => { supMap[sup.id] = sup.name; });
    const purchases = (await db.purchases.toArray()).filter((p) => Number(p.paid_amount) > 0);
    for (const p of purchases) {
      const pMethod = String(p.payment_method || 'cash').toLowerCase();
      entries.push({
        id: p.id,
        type: 'purchase',
        reference: p.reference_number || `PUR-${p.id}`,
        description: `Purchase: ${supMap[p.supplier_id] || 'Supplier'}`,
        payment_method: pMethod === 'credit' && Number(p.paid_amount) > 0 ? 'cash' : pMethod,
        debit: 0,
        credit: Number(p.paid_amount) || 0,
        transaction_time: p.created_at || p.purchase_date || new Date().toISOString(),
        transaction_date: (p.purchase_date || p.created_at || '').slice(0, 10),
      });
    }
  }

  // 6. Supplier Payments
  if (db.supplier_payments) {
    const suppliers = await db.suppliers.toArray();
    const supMap = {};
    suppliers.forEach((sup) => { supMap[sup.id] = sup.name; });
    const sp = (await db.supplier_payments.toArray()).filter((p) => Number(p.amount) > 0);
    for (const p of sp) {
      const isRefundRecv = p.payment_type === 'refund_received';
      entries.push({
        id: p.id,
        type: 'supplier_payment',
        reference: isRefundRecv ? `SP-REF-${String(p.id).padStart(4, '0')}` : `SP-${String(p.id).padStart(4, '0')}`,
        description: isRefundRecv
          ? `Supplier Advance Refund Received: ${supMap[p.supplier_id] || 'Supplier'}${p.notes ? ` (${p.notes})` : ''}`
          : `Supplier Payment: ${supMap[p.supplier_id] || 'Supplier'}${p.notes ? ` (${p.notes})` : ''}`,
        payment_method: String(p.payment_method || 'cash').toLowerCase(),
        debit: isRefundRecv ? (Number(p.amount) || 0) : 0,
        credit: isRefundRecv ? 0 : (Number(p.amount) || 0),
        transaction_time: p.created_at || p.payment_date || new Date().toISOString(),
        transaction_date: (p.payment_date || p.created_at || '').slice(0, 10),
      });
    }
  }

  // 7. Customer Refunds
  if (db.customer_refunds) {
    const cRefunds = (await db.customer_refunds.toArray()).filter((r) => Number(r.refund_amount) > 0);
    for (const r of cRefunds) {
      entries.push({
        id: r.id,
        type: 'customer_refund',
        reference: `REF-C-${String(r.id).padStart(4, '0')}`,
        description: `Customer Refund: ${customerMap[r.customer_id] || 'Customer'}`,
        payment_method: 'cash',
        debit: 0,
        credit: Number(r.refund_amount) || 0,
        transaction_time: r.created_at || r.refund_date || new Date().toISOString(),
        transaction_date: (r.refund_date || r.created_at || '').slice(0, 10),
      });
    }
  }

  // 8. Cash Transactions (Deposit & Withdraw)
  if (db.cash_transactions) {
    const cashTx = (await db.cash_transactions.toArray()).filter((t) => Number(t.amount) > 0);
    for (const ct of cashTx) {
      const isDeposit = (ct.type || '').toLowerCase() === 'deposit';
      const ref = ct.reference || (isDeposit ? `DEP-${String(ct.id).padStart(4, '0')}` : `WTH-${String(ct.id).padStart(4, '0')}`);
      const desc = `${isDeposit ? 'Cash Deposit: ' : 'Cash Withdraw: '}${ct.category || ct.reason || 'General'}${ct.notes ? ` (${ct.notes})` : ''}`;
      entries.push({
        id: ct.id,
        type: isDeposit ? 'cash_deposit' : 'cash_withdraw',
        reference: ref,
        description: desc,
        payment_method: String(ct.payment_method || 'cash').toLowerCase(),
        debit: isDeposit ? (Number(ct.amount) || 0) : 0,
        credit: isDeposit ? 0 : (Number(ct.amount) || 0),
        transaction_time: ct.created_at || ct.transaction_date || new Date().toISOString(),
        transaction_date: (ct.transaction_date || ct.created_at || '').slice(0, 10),
      });
    }
  }

  // 9. Credit Sales (Udhaar extended to customers)
  if (db.sales) {
    const allSales = await db.sales.toArray();
    for (const s of allSales) {
      const tot = Number(s.total) || 0;
      const paid = Number(s.paid_amount) || 0;
      const sMethod = String(s.payment_method || 'cash').toLowerCase();
      if ((tot > paid || sMethod === 'credit') && (tot - paid) > 0) {
        entries.push({
          id: s.id,
          type: 'credit_sale',
          reference: s.invoice_number || `INV-${s.id}`,
          description: `Credit Sale (Udhaar): ${customerMap[s.customer_id] || 'Walk-in Customer'}${paid > 0 ? ` (Bill: ${tot}, Paid: ${paid})` : ''}`,
          payment_method: 'credit',
          debit: Math.round((tot - paid) * 100) / 100,
          credit: 0,
          transaction_time: s.created_at || new Date().toISOString(),
          transaction_date: (s.created_at || '').slice(0, 10),
        });
      }
    }
  }

  // 10. Credit Purchases (Payable to suppliers)
  if (db.purchases) {
    const suppliers = await db.suppliers.toArray();
    const supMap = {};
    suppliers.forEach((sup) => { supMap[sup.id] = sup.name; });
    const allPurchases = await db.purchases.toArray();
    for (const p of allPurchases) {
      const tot = Number(p.total) || 0;
      const paid = Number(p.paid_amount) || 0;
      const pMethod = String(p.payment_method || 'cash').toLowerCase();
      if ((tot > paid || pMethod === 'credit') && (tot - paid) > 0) {
        entries.push({
          id: p.id,
          type: 'credit_purchase',
          reference: p.reference_number || `PUR-${p.id}`,
          description: `Credit Purchase (Payable): ${supMap[p.supplier_id] || 'Supplier'}${paid > 0 ? ` (Bill: ${tot}, Paid: ${paid})` : ''}`,
          payment_method: 'credit',
          debit: 0,
          credit: Math.round((tot - paid) * 100) / 100,
          transaction_time: p.created_at || p.purchase_date || new Date().toISOString(),
          transaction_date: (p.purchase_date || p.created_at || '').slice(0, 10),
        });
      }
    }
  }

  // Sort chronological
  entries.sort((a, b) => {
    const t1 = new Date(a.transaction_time || 0).getTime();
    const t2 = new Date(b.transaction_time || 0).getTime();
    if (t1 === t2) return (a.id || 0) - (b.id || 0);
    return t1 - t2;
  });

  const isMethodFiltered = method !== '' && method !== 'all';
  let runningBalance = 0;
  let allTimeCashDebit = 0;
  let allTimeCashCredit = 0;
  let periodDebit = 0;
  let periodCredit = 0;
  let periodExpenses = 0;
  let periodCashDeposits = 0;
  let periodCashWithdrawals = 0;
  const filteredEntries = [];

  for (const entry of entries) {
    const entryMethod = String(entry.payment_method || 'cash').toLowerCase();
    const entryType = String(entry.type || '');
    const debit = Number(entry.debit) || 0;
    const credit = Number(entry.credit) || 0;

    if (entryMethod === 'cash') {
      allTimeCashDebit += debit;
      allTimeCashCredit += credit;
    }

    if ((entryType === 'credit_sale' || entryType === 'credit_purchase') && method !== 'credit') {
      continue;
    }

    if (isMethodFiltered) {
      if (method === 'deposit' || method === 'cash_deposit') {
        if (entryType !== 'cash_deposit') continue;
      } else if (method === 'withdraw' || method === 'cash_withdraw' || method === 'withdrawal') {
        if (entryType !== 'cash_withdraw') continue;
      } else if (method === 'credit') {
        if (entryMethod !== 'credit' && entryType !== 'credit_sale' && entryType !== 'credit_purchase') continue;
      } else {
        if (entryMethod !== method) continue;
      }
    }

    runningBalance += (debit - credit);
    entry.balance = runningBalance;

    const entryDate = entry.transaction_date || '';
    let inRange = true;
    if (dateFrom && entryDate < dateFrom) inRange = false;
    if (dateTo && entryDate > dateTo) inRange = false;

    if (inRange) {
      periodDebit += debit;
      periodCredit += credit;
      if (entryType === 'expense' || entryType === 'staff_salary') {
        periodExpenses += credit;
      }
      if (entryType === 'cash_deposit') {
        periodCashDeposits += debit;
      } else if (entryType === 'cash_withdraw') {
        periodCashWithdrawals += credit;
      }
      filteredEntries.push(entry);
    }
  }

  const allTimeCashBalance = allTimeCashDebit - allTimeCashCredit;
  let availableBalance = runningBalance;
  if (method === 'cash' || method === 'all' || method === '') {
    availableBalance = allTimeCashBalance;
  } else if (method === 'deposit' || method === 'cash_deposit') {
    availableBalance = periodCashDeposits;
  } else if (method === 'withdraw' || method === 'cash_withdraw' || method === 'withdrawal') {
    availableBalance = periodCashWithdrawals;
  }
  const totalCount = filteredEntries.length;
  const reversed = [...filteredEntries].reverse();
  const paginated = reversed.slice(offset, offset + limit);

  // Card / digital sales include only funds actually paid through a digital
  // channel. A credit invoice may have a paid down-payment, but it is not a
  // card/digital sale.
  const saleInPeriod = (sale) => {
    const saleDate = String(sale.created_at || '').slice(0, 10);
    return (!dateFrom || saleDate >= dateFrom) && (!dateTo || saleDate <= dateTo);
  };
  const paidSalesInPeriod = sales.filter((sale) => saleInPeriod(sale));
  const cashSales = paidSalesInPeriod
    .filter((sale) => String(sale.payment_method || 'cash').toLowerCase() === 'cash')
    .reduce((sum, sale) => sum + Math.min(Number(sale.paid_amount) || 0, Number(sale.total) || 0), 0);
  const cardSales = paidSalesInPeriod
    .filter((sale) => ['card', 'online', 'bank'].includes(String(sale.payment_method || 'cash').toLowerCase()))
    .reduce((sum, sale) => sum + Math.min(Number(sale.paid_amount) || 0, Number(sale.total) || 0), 0);
  const digitalMethods = ['card', 'online', 'bank'];
  const allTimeDigitalBalance = entries
    .filter((entry) => digitalMethods.includes(String(entry.payment_method || 'cash').toLowerCase()))
    .reduce((sum, entry) => sum + (Number(entry.debit) || 0) - (Number(entry.credit) || 0), 0);
  const showDigitalBalance = method === 'all' && !dateFrom && !dateTo;

  return {
    dateFrom,
    dateTo,
    method,
    totals: {
      debit: periodDebit,
      credit: periodCredit,
      expenses: periodExpenses,
      availableCash: availableBalance,
      runningBalance,
      cashSales: method === 'all' || method === 'cash' ? cashSales : 0,
      cardSales: showDigitalBalance
        ? allTimeDigitalBalance
        : (method === 'all' || digitalMethods.includes(method) ? cardSales : 0),
      cashDeposits: periodCashDeposits,
      cashWithdrawals: periodCashWithdrawals,
    },
    entries: paginated,
    pagination: {
      total: totalCount,
      page,
      limit,
      totalPages: Math.ceil(totalCount / limit) || 1,
    },
  };
}

async function processCheckout(data) {
  const invoiceNumber = `INV-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${String(Math.floor(Math.random() * 9999) + 1).padStart(4, '0')}`;

  const subtotal = data.items.reduce((sum, item) => sum + item.unit_price * item.quantity, 0);
  const taxAmount = subtotal * ((data.tax_rate || 0) / 100);
  const total = subtotal - (data.discount || 0) + taxAmount;

  const saleId = await db.sales.add({
    invoice_number: invoiceNumber,
    customer_id: data.customer_id || null,
    user_id: data.user_id || 1,
    subtotal,
    discount: data.discount || 0,
    tax: taxAmount,
    total,
    payment_method: data.payment_method || 'cash',
    payment_status: !data.paid_amount || data.paid_amount <= 0 ? 'unpaid' : data.paid_amount >= total ? 'paid' : 'partial',
    paid_amount: Math.min(data.paid_amount || 0, total),
    checkout_paid_amount: Math.min(data.paid_amount || 0, total),
    notes: data.notes || null,
    created_at: new Date().toISOString(),
  });

  for (const item of data.items) {
    await db.sale_items.add({
      sale_id: saleId,
      medicine_id: item.medicine_id,
      batch_id: null,
      quantity: item.quantity,
      unit_price: item.unit_price,
      discount: item.discount || 0,
      total: item.quantity * item.unit_price,
    });

    const batches = await db.batches.where('medicine_id').equals(item.medicine_id).toArray();
    const sorted = batches
      .filter((b) => b.quantity > 0 && (!b.expiry_date || new Date(b.expiry_date) >= new Date()))
      .sort((a, b) => {
        if (!a.expiry_date) return 1;
        if (!b.expiry_date) return -1;
        return new Date(a.expiry_date) - new Date(b.expiry_date);
      });

    let remaining = item.quantity;
    for (const batch of sorted) {
      if (remaining <= 0) break;
      const deduct = Math.min(batch.quantity, remaining);
      await db.batches.update(batch.id, { quantity: batch.quantity - deduct });
      remaining -= deduct;
    }
  }

  if (data.customer_id && data.payment_method === 'credit') {
    const customer = await db.customers.get(data.customer_id);
    if (customer) {
      await db.customers.update(data.customer_id, {
        balance: (customer.balance || 0) + total - (data.paid_amount || 0),
      });
    }
  }

  return { invoice_number: invoiceNumber, sale_id: saleId, total };
}

async function searchMedicinesContext(query, currency) {
  if (!query || !query.trim()) return 'No specific medicine query provided.';
  try {
    const rawWords = query.toLowerCase().split(/[\s,.\?!-_]+/);
    const stopWords = new Set(['do', 'we', 'have', 'is', 'are', 'there', 'any', 'in', 'stock', 'the', 'a', 'an', 'what', 'check', 'price', 'of', 'for', 'please', 'tell', 'me', 'how', 'much', 'many', 'available', 'got']);
    const keywords = rawWords.filter((w) => w.length >= 2 && !stopWords.has(w));

    const medicines = await db.medicines.toArray();
    const categories = await db.categories.toArray();
    const catMap = Object.fromEntries(categories.map((c) => [c.id, c.name]));

    const matched = medicines.filter((m) => {
      if (m.is_active === 0 || m.is_active === false) return false;
      const targetStr = `${m.name || ''} ${m.generic_name || ''} ${m.barcode || ''} ${m.sku || ''}`.toLowerCase();
      if (keywords.length > 0) {
        return keywords.some((k) => targetStr.includes(k));
      }
      return targetStr.includes(query.toLowerCase().trim());
    });

    if (matched.length === 0) {
      return `Search query: "${query}" -> NOT FOUND in pharmacy database (0 matches).`;
    }

    const lines = await Promise.all(
      matched.slice(0, 10).map(async (m) => {
        const batches = await db.batches.where('medicine_id').equals(m.id).toArray();
        const totalStock = batches.reduce((sum, b) => sum + (b.quantity || 0), 0);
        const generic = m.generic_name ? ` (Generic: ${m.generic_name})` : '';
        const cat = catMap[m.category_id] ? ` [${catMap[m.category_id]}]` : '';
        const sellingPrice = m.selling_price || (batches[0]?.selling_price || 0);
        const stockStatus = totalStock > 0
          ? `IN STOCK: ${totalStock} units available at ${currency} ${Number(sellingPrice).toFixed(2)}`
          : 'OUT OF STOCK: 0 units';
        return `- ${m.name}${generic}${cat} => ${stockStatus}`;
      })
    );

    return lines.join('\n');
  } catch (err) {
    return 'Unable to search medicines: ' + err.message;
  }
}

async function getLowStockContext(threshold = 10) {
  try {
    const medicines = await db.medicines.toArray();
    const categories = await db.categories.toArray();
    const catMap = Object.fromEntries(categories.map((c) => [c.id, c.name]));

    const lowList = [];
    for (const m of medicines) {
      if (m.is_active === 0 || m.is_active === false) continue;
      const batches = await db.batches.where('medicine_id').equals(m.id).toArray();
      const stock = batches.reduce((sum, b) => sum + (b.quantity || 0), 0);
      const reorderLevel = Number(m.reorder_level) || Number(threshold) || 10;
      if (stock <= reorderLevel) {
        lowList.push({
          name: m.name,
          generic: m.generic_name ? ` (${m.generic_name})` : '',
          cat: catMap[m.category_id] ? ` [${catMap[m.category_id]}]` : '',
          stock,
          reorderLevel,
        });
      }
    }

    if (lowList.length === 0) {
      return 'All medicines currently have adequate stock above their reorder level.';
    }

    lowList.sort((a, b) => a.stock - b.stock);
    return lowList.slice(0, 25).map((m) => `- ${m.name}${m.generic}${m.cat}: ${m.stock} units left (Reorder level: ${m.reorderLevel})`).join('\n');
  } catch {
    return 'Unable to retrieve low stock data.';
  }
}

async function getExpiryContext(days = 90) {
  try {
    const now = new Date();
    const targetDate = new Date();
    targetDate.setDate(now.getDate() + Number(days));

    const todayStr = now.toISOString().slice(0, 10);
    const targetStr = targetDate.toISOString().slice(0, 10);

    const batches = await db.batches.toArray();
    const medicines = await db.medicines.toArray();
    const medMap = Object.fromEntries(medicines.map((m) => [m.id, m.name]));

    const expiringBatches = batches.filter((b) => {
      if (!b.expiry_date || (b.quantity || 0) <= 0) return false;
      return b.expiry_date >= todayStr && b.expiry_date <= targetStr;
    });

    if (expiringBatches.length === 0) {
      return `No batches are expiring within the next ${days} days.`;
    }

    expiringBatches.sort((a, b) => new Date(a.expiry_date) - new Date(b.expiry_date));

    return expiringBatches.slice(0, 25).map((b) => {
      const expDate = new Date(b.expiry_date);
      const diffTime = expDate - now;
      const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const medName = medMap[b.medicine_id] || 'Unknown Medicine';
      return `- ${medName} (Batch ${b.batch_number || 'N/A'}): ${b.quantity} units, Expiry: ${b.expiry_date} (${daysLeft} days left)`;
    }).join('\n');
  } catch {
    return 'Unable to retrieve expiry data.';
  }
}

async function getSalesContext() {
  try {
    const sales = await db.sales.toArray();
    const today = new Date().toISOString().slice(0, 10);
    const monthStart = today.slice(0, 7);

    const todaySales = sales.filter((s) => (s.created_at || '').startsWith(today));
    const monthSales = sales.filter((s) => (s.created_at || '').startsWith(monthStart));

    const todayTotal = todaySales.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0);
    const monthTotal = monthSales.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0);

    return {
      today: { orders: todaySales.length, total: todayTotal.toFixed(2) },
      month: { orders: monthSales.length, total: monthTotal.toFixed(2) },
    };
  } catch {
    return {
      today: { orders: 0, total: '0.00' },
      month: { orders: 0, total: '0.00' },
    };
  }
}

async function getTopSellersContext() {
  try {
    const today = new Date().toISOString().slice(0, 10);
    const monthStart = today.slice(0, 7);
    const sales = await db.sales.toArray();
    const monthSaleIds = new Set(sales.filter((s) => (s.created_at || '').startsWith(monthStart)).map((s) => s.id));

    const saleItems = await db.sale_items.toArray();
    const monthItems = saleItems.filter((si) => monthSaleIds.has(si.sale_id));

    const medTotals = {};
    for (const item of monthItems) {
      if (!medTotals[item.medicine_id]) {
        medTotals[item.medicine_id] = { qty: 0, revenue: 0 };
      }
      medTotals[item.medicine_id].qty += Number(item.quantity) || 0;
      medTotals[item.medicine_id].revenue += Number(item.total) || 0;
    }

    const medicines = await db.medicines.toArray();
    const medMap = Object.fromEntries(medicines.map((m) => [m.id, m.name]));

    const sorted = Object.entries(medTotals)
      .map(([id, stat]) => ({ name: medMap[id] || `Medicine #${id}`, ...stat }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);

    if (sorted.length === 0) return 'No sales recorded yet this month.';
    return sorted.map((s) => `- ${s.name}: ${s.qty} units sold (Revenue: ${s.revenue.toFixed(2)})`).join('\n');
  } catch {
    return 'Unable to retrieve top sellers.';
  }
}

async function getFinancialContext() {
  try {
    const customers = await db.customers.toArray();
    const suppliers = await db.suppliers.toArray();

    const receivables = customers
      .filter((c) => parseFloat(c.balance) > 0)
      .reduce((sum, c) => sum + parseFloat(c.balance || 0), 0);

    const payables = suppliers
      .filter((s) => parseFloat(s.balance) > 0)
      .reduce((sum, s) => sum + parseFloat(s.balance || 0), 0);

    return {
      receivables: receivables.toFixed(2),
      payables: payables.toFixed(2),
    };
  } catch {
    return { receivables: '0.00', payables: '0.00' };
  }
}

async function getCategoriesContext() {
  try {
    const categories = await db.categories.toArray();
    if (categories.length === 0) return 'None';
    return categories.map((c) => c.name).filter(Boolean).join(', ');
  } catch {
    return 'Unable to retrieve categories.';
  }
}

async function callGeminiApi(apiKey, model, systemInstruction, chatHistory, userMessage) {
  const candidateModels = Array.from(new Set([
    model,
    'gemini-1.5-flash',
    'gemini-2.0-flash',
    'gemini-2.5-flash',
    'gemini-1.5-pro',
  ])).filter(Boolean);

  const formattedContents = [];
  if (Array.isArray(chatHistory)) {
    for (const msg of chatHistory) {
      if (!msg?.content) continue;
      const role = msg.role === 'user' ? 'user' : 'model';
      formattedContents.push({
        role,
        parts: [{ text: String(msg.content) }],
      });
    }
  }
  if (userMessage) {
    formattedContents.push({
      role: 'user',
      parts: [{ text: String(userMessage) }],
    });
  }

  let lastError = null;
  for (const m of candidateModels) {
    try {
      const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(m)}:generateContent?key=${encodeURIComponent(apiKey.trim())}`;

      const payload = {
        contents: formattedContents,
        generationConfig: {
          temperature: 0.7,
        },
      };

      if (systemInstruction) {
        payload.system_instruction = {
          parts: [{ text: systemInstruction }],
        };
      }

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || data.error) {
        const errMsg = data?.error?.message || `HTTP ${res.status}: ${res.statusText}`;
        if (errMsg.includes('API_KEY_INVALID') || errMsg.includes('API key not valid') || errMsg.includes('API_KEY_SERVICE_BLOCKED')) {
          throw new Error('Invalid Gemini API Key. Please check your API key from Google AI Studio in Settings.');
        }
        if (errMsg.includes('RESOURCE_EXHAUSTED') || errMsg.includes('Quota exceeded')) {
          throw new Error('Gemini API quota exceeded. Please try again later or check your Gemini quota.');
        }
        if (res.status === 404 || errMsg.includes('models/') || errMsg.includes('not found')) {
          lastError = new Error(errMsg);
          continue;
        }
        throw new Error(errMsg);
      }

      const reply = data?.candidates?.[0]?.content?.parts?.[0]?.text;
      if (reply) {
        return { reply, model: m };
      }
      if (data?.candidates?.[0]?.finishReason === 'SAFETY') {
        return { reply: 'The response was blocked due to safety guidelines.', model: m };
      }
      return { reply: 'No response text generated.', model: m };
    } catch (err) {
      if (err.message.includes('Invalid Gemini API Key') || err.message.includes('quota exceeded')) {
        throw err;
      }
      lastError = err;
    }
  }

  throw lastError || new Error('Failed to connect to Google Gemini API.');
}

const localApi = {
  get: async (endpoint) => {
    const { parts, id } = parseEndpoint(endpoint);
    const params = parseQueryParams(endpoint);
    const resource = parts[0];

    if (resource === 'daily-closings') {
      const authUser = getCurrentAuthUser();
      const userRole = (authUser?.role || 'cashier').toLowerCase();
      const currentUserId = Number(authUser?.id || 1);
      const isCashier = userRole === 'cashier';
      const targetDate = (params.date || new Date().toISOString().slice(0, 10)).trim();

      const allSales = await db.sales.toArray();
      const users = db.users ? await db.users.toArray() : [];
      const userMap = {};
      users.forEach((u) => { userMap[Number(u.id)] = u; });

      // Filter sales by date (match YYYY-MM-DD)
      const targetSales = allSales.filter((s) => {
        const saleDate = (s.created_at || '').slice(0, 10);
        return saleDate === targetDate;
      });

      // Fetch saved closings for target date
      const savedClosings = db.daily_closings
        ? await db.daily_closings.where('business_date').equals(targetDate).toArray()
        : [];
      const closingsMap = {};
      savedClosings.forEach((c) => { closingsMap[Number(c.staff_id)] = c; });

      if (parts[1] === 'my-status') {
        const mySales = targetSales.filter((s) => Number(s.user_id || 1) === currentUserId);
        const myClosing = closingsMap[currentUserId];

        let invoiceCount = mySales.length;
        let totalSales = 0;
        let cashSales = 0;
        let cardSales = 0;
        let onlineSales = 0;
        let creditSales = 0;

        mySales.forEach((s) => {
          const tot = Number(s.total || 0);
          const paid = Math.min(tot, Math.max(0, Number(s.paid_amount || 0)));
          const creditDue = Math.max(0, tot - paid);
          totalSales += tot;
          creditSales += creditDue;

          const method = String(s.payment_method || 'cash').toLowerCase();
          if (method === 'card') cardSales += paid;
          else if (method === 'online' || method === 'bank') onlineSales += paid;
          else cashSales += paid;
        });

        if (myClosing) {
          return {
            date: targetDate,
            status: myClosing.status || 'CLOSED',
            closed_at: myClosing.closed_at || null,
            notes: myClosing.notes || '',
            staff_id: currentUserId,
            staff_name: myClosing.staff_name || authUser?.full_name || authUser?.username || 'Cashier',
            role: myClosing.role || authUser?.role || 'cashier',
            invoice_count: myClosing.invoice_count ?? invoiceCount,
            total_sales: myClosing.total_sales ?? totalSales,
            cash_sales: myClosing.cash_sales ?? cashSales,
            card_sales: myClosing.card_sales ?? cardSales,
            online_sales: myClosing.online_sales ?? onlineSales,
            credit_sales: myClosing.credit_sales ?? creditSales,
          };
        }

        return {
          date: targetDate,
          status: 'OPEN',
          closed_at: null,
          notes: '',
          staff_id: currentUserId,
          staff_name: authUser?.full_name || authUser?.username || 'Cashier',
          role: authUser?.role || 'cashier',
          invoice_count: invoiceCount,
          total_sales: totalSales,
          cash_sales: cashSales,
          card_sales: cardSales,
          online_sales: onlineSales,
          credit_sales: creditSales,
        };
      }

      // Root /daily-closings (Admin / List view)
      const staffMap = {};

      // Aggregate sales by user
      targetSales.forEach((s) => {
        const sId = Number(s.user_id || 1);
        if (!staffMap[sId]) {
          const uObj = userMap[sId] || {};
          staffMap[sId] = {
            staff_id: sId,
            staff_name: uObj.full_name || uObj.username || s.cashier_name || 'Staff',
            username: uObj.username || s.cashier_username || '',
            role: (uObj.role || s.cashier_role || 'Cashier'),
            invoice_count: 0,
            total_sales: 0,
            cash_sales: 0,
            card_sales: 0,
            online_sales: 0,
            credit_sales: 0,
          };
        }
        const st = staffMap[sId];
        st.invoice_count += 1;
        const tot = Number(s.total || 0);
        const paid = Math.min(tot, Math.max(0, Number(s.paid_amount || 0)));
        const creditDue = Math.max(0, tot - paid);
        st.total_sales += tot;
        st.credit_sales += creditDue;

        const method = String(s.payment_method || 'cash').toLowerCase();
        if (method === 'card') st.card_sales += paid;
        else if (method === 'online' || method === 'bank') st.online_sales += paid;
        else st.cash_sales += paid;
      });

      // Merge saved closings (including staff with 0 sales if closed)
      savedClosings.forEach((c) => {
        const sId = Number(c.staff_id);
        if (!staffMap[sId]) {
          staffMap[sId] = {
            staff_id: sId,
            staff_name: c.staff_name || 'Staff',
            username: '',
            role: c.role || 'Cashier',
            invoice_count: Number(c.invoice_count || 0),
            total_sales: Number(c.total_sales || 0),
            cash_sales: Number(c.cash_sales || 0),
            card_sales: Number(c.card_sales || 0),
            online_sales: Number(c.online_sales || 0),
            credit_sales: Number(c.credit_sales || 0),
          };
        }
      });

      let cashiersList = Object.values(staffMap).map((st) => {
        const cRec = closingsMap[st.staff_id];
        const isClosed = cRec && String(cRec.status).toUpperCase() === 'CLOSED';
        return {
          id: cRec?.id || null,
          staff_id: st.staff_id,
          staff_name: cRec?.staff_name || st.staff_name,
          username: st.username,
          role: (cRec?.role || st.role || 'Cashier'),
          invoice_count: cRec?.invoice_count ?? st.invoice_count,
          total_sales: cRec?.total_sales ?? st.total_sales,
          cash_sales: cRec?.cash_sales ?? st.cash_sales,
          card_sales: cRec?.card_sales ?? st.card_sales,
          online_sales: cRec?.online_sales ?? st.online_sales,
          credit_sales: cRec?.credit_sales ?? st.credit_sales,
          closed_by: cRec?.closed_by || null,
          closed_by_name: cRec?.closed_by ? (userMap[Number(cRec.closed_by)]?.full_name || 'Admin') : null,
          closed_at: cRec?.closed_at || null,
          status: isClosed ? 'CLOSED' : 'OPEN',
          notes: cRec?.notes || null,
        };
      });

      if (isCashier) {
        cashiersList = cashiersList.filter((c) => c.staff_id === currentUserId);
      }

      const totalSales = cashiersList.reduce((sum, c) => sum + Number(c.total_sales || 0), 0);
      const totalInvoices = cashiersList.reduce((sum, c) => sum + Number(c.invoice_count || 0), 0);
      const closedCashiers = cashiersList.filter((c) => c.status === 'CLOSED').length;
      const totalCashiers = cashiersList.length;

      return {
        date: targetDate,
        summary: {
          total_sales: totalSales,
          total_invoices: totalInvoices,
          total_cashiers: totalCashiers,
          closed_cashiers: closedCashiers,
          pending_closings: totalCashiers - closedCashiers,
        },
        cashiers: cashiersList,
      };
    }

    if (resource === 'cash-transactions') {
      if (parts[1] && !isNaN(parts[1])) {
        const txId = Number(parts[1]);
        const tx = db.cash_transactions ? await db.cash_transactions.get(txId) : null;
        if (!tx) throw new Error('Transaction not found');
        return tx;
      }

      const allTx = db.cash_transactions ? await db.cash_transactions.toArray() : [];
      const typeFilter = (params.type || '').toLowerCase();
      const dateFrom = params.date_from || '';
      const dateTo = params.date_to || '';
      const search = (params.search || '').toLowerCase();
      const page = Math.max(1, parseInt(params.page, 10) || 1);
      const limit = Math.max(1, Math.min(200, parseInt(params.limit, 10) || 20));
      const offset = (page - 1) * limit;

      let filtered = allTx;
      if (typeFilter && typeFilter !== 'all') {
        filtered = filtered.filter((t) => (t.type || '').toLowerCase() === typeFilter);
      }
      if (dateFrom) {
        filtered = filtered.filter((t) => (t.transaction_date || (t.created_at || '').slice(0, 10)) >= dateFrom);
      }
      if (dateTo) {
        filtered = filtered.filter((t) => (t.transaction_date || (t.created_at || '').slice(0, 10)) <= dateTo);
      }
      if (search) {
        filtered = filtered.filter((t) =>
          (t.reference && t.reference.toLowerCase().includes(search)) ||
          (t.category && t.category.toLowerCase().includes(search)) ||
          (t.account_name && t.account_name.toLowerCase().includes(search)) ||
          (t.notes && t.notes.toLowerCase().includes(search))
        );
      }

      filtered.sort((a, b) => {
        const t1 = new Date(b.created_at || b.transaction_date || 0).getTime();
        const t2 = new Date(a.created_at || a.transaction_date || 0).getTime();
        return t1 - t2;
      });

      const totalCount = filtered.length;
      const paginated = filtered.slice(offset, offset + limit);

      const totalDeposits = allTx
        .filter((t) => (t.type || '').toLowerCase() === 'deposit')
        .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
      const totalWithdrawals = allTx
        .filter((t) => (t.type || '').toLowerCase() === 'withdraw')
        .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

      return {
        transactions: paginated,
        pagination: {
          total: totalCount,
          page,
          limit,
          totalPages: Math.ceil(totalCount / limit) || 1,
        },
        summary: {
          total_deposits: totalDeposits,
          total_withdrawals: totalWithdrawals,
          net_cash_flow: totalDeposits - totalWithdrawals,
        },
      };
    }

    if (resource === 'dashboard') return computeDashboard();
    if (resource === 'accounting') return computeAccounting(params);

    if (resource === 'reports') {
      const sub = parts[1];
      if (sub === 'stock') {
        const all = await db.medicines.toArray();
        const enriched = await Promise.all(all.map(enrichMedicine));
        const stockValue = enriched.reduce((sum, m) => sum + ((m.total_stock || 0) * (m.avg_cost_price || 0)), 0);
        const retailValue = enriched.reduce((sum, m) => sum + ((m.total_stock || 0) * (m.selling_price || 0)), 0);
        const inStock = enriched.filter((m) => (m.total_stock || 0) > 0);
        const lowStock = enriched.filter((m) => (m.total_stock || 0) > 0 && (m.total_stock || 0) <= (m.reorder_level || 0));
        const outOfStock = enriched.filter((m) => (m.total_stock || 0) === 0);

        let filtered = inStock;
        const status = params.status || 'in_stock';
        if (status === 'low_stock') filtered = lowStock;
        else if (status === 'out_of_stock') filtered = outOfStock;
        else if (status === 'all') filtered = enriched;

        if (params.search) {
          const q = String(params.search).toLowerCase();
          filtered = filtered.filter((m) => (m.name && m.name.toLowerCase().includes(q)) || (m.generic_name && m.generic_name.toLowerCase().includes(q)));
        }

        const page = Math.max(1, parseInt(params.page, 10) || 1);
        const limit = Math.max(1, parseInt(params.limit, 10) || 50);
        const total = filtered.length;
        const totalPages = Math.ceil(total / limit) || 1;
        const paged = filtered.slice((page - 1) * limit, page * limit).map((m) => ({
          id: m.id,
          name: m.name,
          generic_name: m.generic_name || '',
          barcode: m.barcode || '',
          sku: m.sku || '',
          category_name: m.category_name || 'General',
          total_stock: m.total_stock || 0,
          reorder_level: m.reorder_level || 0,
          cost_price: m.avg_cost_price || 0,
          selling_price: m.selling_price || 0,
          cost_value: (m.total_stock || 0) * (m.avg_cost_price || 0),
          retail_value: (m.total_stock || 0) * (m.selling_price || 0),
          status: (m.total_stock || 0) <= 0 ? 'out_of_stock' : (m.total_stock || 0) <= (m.reorder_level || 0) ? 'low_stock' : 'in_stock',
          nearest_expiry: m.nearest_expiry || null,
        }));

        return {
          summary: {
            stockValue,
            retailValue,
            potentialProfit: Math.max(0, retailValue - stockValue),
            margin: retailValue > 0 ? Math.round(((retailValue - stockValue) / retailValue) * 1000) / 10 : 0,
            inStockItems: inStock.length,
            totalMedicines: enriched.length,
            lowStockCount: lowStock.length,
            outOfStockCount: outOfStock.length,
          },
          items: paged,
          pagination: { page, limit, total, totalPages },
        };
      }
      if (sub === 'financial') {
        const sales = await db.sales.toArray();
        const purchases = await db.purchases.toArray();
        const expenses = (await db.expenses.toArray()).filter((e) => e.category !== 'Advance Payment');
        const suppliers = await db.suppliers.toArray();

        const from = params.date_from || '';
        const to = params.date_to || '';

        const fSales = sales.filter((s) => (!from || (s.created_at || '').slice(0, 10) >= from) && (!to || (s.created_at || '').slice(0, 10) <= to));
        const fPurchases = purchases.filter((p) => (!from || (p.purchase_date || p.created_at || '').slice(0, 10) >= from) && (!to || (p.purchase_date || p.created_at || '').slice(0, 10) <= to));
        const fExpenses = expenses.filter((e) => (!from || (e.date || e.created_at || '').slice(0, 10) >= from) && (!to || (e.date || e.created_at || '').slice(0, 10) <= to));

        const totalSales = fSales.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0);
        const paidSales = fSales.filter((s) => s.payment_status === 'paid').reduce((sum, s) => sum + (parseFloat(s.paid_amount || s.total) || 0), 0);
        const creditSales = fSales.reduce((sum, s) => sum + Math.max(0, (parseFloat(s.total) || 0) - (parseFloat(s.paid_amount) || 0)), 0);

        const totalPurchases = fPurchases.reduce((sum, p) => sum + (parseFloat(p.total) || 0), 0);
        const paidPurchases = fPurchases.reduce((sum, p) => sum + (parseFloat(p.paid_amount || 0)), 0);
        const creditPurchases = fPurchases.reduce((sum, p) => sum + Math.max(0, (parseFloat(p.total) || 0) - (parseFloat(p.paid_amount) || 0)), 0);

        const totalExpenses = fExpenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

        const catMap = {};
        fExpenses.forEach((e) => {
          catMap[e.category] = (catMap[e.category] || 0) + (parseFloat(e.amount) || 0);
        });
        const categories = Object.entries(catMap).map(([category, total]) => ({ category, total })).sort((a, b) => b.total - a.total);

        const grossProfit = totalSales - totalPurchases;
        const netProfit = grossProfit - totalExpenses;
        const grossMargin = totalSales > 0 ? Math.round((grossProfit / totalSales) * 1000) / 10 : 0;
        const netMargin = totalSales > 0 ? Math.round((netProfit / totalSales) * 1000) / 10 : 0;

        return {
          date_from: from,
          date_to: to,
          sales: {
            total: totalSales,
            paid: paidSales,
            credit: creditSales,
            count: fSales.length,
            items: fSales.slice(0, 100),
          },
          purchases: {
            total: totalPurchases,
            paid: paidPurchases,
            credit: creditPurchases,
            count: fPurchases.length,
            items: fPurchases.slice(0, 100),
          },
          expenses: {
            total: totalExpenses,
            count: fExpenses.length,
            categories,
            items: fExpenses.slice(0, 100),
          },
          suppliers_count: suppliers.length,
          profit: {
            revenue: totalSales,
            cogs: totalPurchases,
            expenses: totalExpenses,
            grossProfit,
            netProfit,
            grossMargin,
            netMargin,
          },
        };
      }
      if (sub === 'sold-items') {
        const sales = await db.sales.toArray();
        const medicines = await db.medicines.toArray();
        const saleItems = await db.sale_items.toArray();
        const batches = await db.batches.toArray();
        const categories = await db.categories.toArray();

        const catMap = {};
        categories.forEach((c) => { catMap[c.id] = c.name; });

        const from = params.date_from || '';
        const to = params.date_to || '';

        // Filter sales within date range
        const matchingSales = sales.filter((s) => {
          const d = (s.created_at || '').slice(0, 10);
          return (!from || d >= from) && (!to || d <= to);
        });
        const matchingSaleIds = new Set(matchingSales.map((s) => String(s.id)));

        // Collect all sale items from db.sale_items table plus any embedded items in db.sales
        let allSaleItems = [...saleItems];
        sales.forEach((s) => {
          if (Array.isArray(s.items) && s.items.length > 0) {
            s.items.forEach((it) => {
              allSaleItems.push({
                ...it,
                sale_id: s.id,
                medicine_id: it.medicine_id,
                quantity: it.quantity || it.unit_quantity || 1,
                total: it.total !== undefined ? it.total : ((it.quantity || 1) * (it.unit_price || 0)),
                unit_price: it.unit_price || 0,
                batch_id: it.batch_id,
              });
            });
          }
        });

        // Filter sale items belonging to matching sales
        const filteredSaleItems = allSaleItems.filter((si) => matchingSaleIds.has(String(si.sale_id)));

        // Aggregate by medicine_id
        const itemMap = {};
        filteredSaleItems.forEach((si) => {
          const medId = String(si.medicine_id);
          if (!itemMap[medId]) {
            itemMap[medId] = {
              medicine_id: si.medicine_id,
              sold_qty: 0,
              total_amount: 0,
              total_cost: 0,
              invoices: new Set(),
            };
          }
          const qty = parseFloat(si.quantity) || 0;
          const totalAmt = parseFloat(si.total) || 0;
          itemMap[medId].sold_qty += qty;
          itemMap[medId].total_amount += totalAmt;

          const batch = batches.find((b) => String(b.id) === String(si.batch_id));
          const med = medicines.find((m) => String(m.id) === String(si.medicine_id));
          const cost = (batch && Number(batch.cost_price) > 0) ? Number(batch.cost_price) : (Number(med?.avg_cost_price) || 0);
          itemMap[medId].total_cost += qty * cost;
          itemMap[medId].invoices.add(String(si.sale_id));
        });

        // Compute current remaining stock for each medicine from batches or total_stock
        const stockMap = {};
        batches.filter((b) => (b.quantity || 0) > 0).forEach((b) => {
          const mKey = String(b.medicine_id);
          stockMap[mKey] = (stockMap[mKey] || 0) + (parseFloat(b.quantity) || 0);
        });

        // Format item rows
        let rows = Object.values(itemMap).map((agg) => {
          const med = medicines.find((m) => String(m.id) === String(agg.medicine_id)) || {};
          const remaining_stock = stockMap[String(agg.medicine_id)] !== undefined ? stockMap[String(agg.medicine_id)] : (Number(med.total_stock) || 0);
          const initial_stock = remaining_stock + agg.sold_qty;
          const total_amount = agg.total_amount;
          const total_cost = agg.total_cost;
          const profit = total_amount - total_cost;
          const avg_price = agg.sold_qty > 0 ? Math.round((total_amount / agg.sold_qty) * 100) / 100 : (med.selling_price || 0);
          const margin = total_amount > 0 ? Math.round((profit / total_amount) * 1000) / 10 : 0;
          const reorder_level = med.reorder_level || 0;

          let stock_status = 'in_stock';
          if (remaining_stock <= 0) stock_status = 'out_of_stock';
          else if (remaining_stock <= reorder_level) stock_status = 'low_stock';

          return {
            id: agg.medicine_id,
            name: med.name || 'Unknown Medicine',
            generic_name: med.generic_name || '',
            barcode: med.barcode || '',
            sku: med.sku || '',
            unit: med.unit || 'pcs',
            category_name: catMap[med.category_id] || 'General',
            sold_qty: agg.sold_qty,
            remaining_stock,
            initial_stock,
            avg_price,
            total_amount,
            total_cost,
            profit,
            margin,
            invoices_count: agg.invoices.size,
            stock_status,
          };
        });

        // Apply search filter if any
        if (params.search) {
          const q = String(params.search).toLowerCase();
          rows = rows.filter((r) =>
            (r.name && r.name.toLowerCase().includes(q)) ||
            (r.generic_name && r.generic_name.toLowerCase().includes(q)) ||
            (r.barcode && r.barcode.toLowerCase().includes(q)) ||
            (r.sku && r.sku.toLowerCase().includes(q))
          );
        }

        // Sort by sold_qty DESC
        rows.sort((a, b) => b.sold_qty - a.sold_qty || b.total_amount - a.total_amount);

        // Summary calculations
        const totalSoldQty = rows.reduce((sum, r) => sum + r.sold_qty, 0);
        const totalSoldAmount = rows.reduce((sum, r) => sum + r.total_amount, 0);
        const totalSoldCost = rows.reduce((sum, r) => sum + r.total_cost, 0);
        const totalProfit = totalSoldAmount - totalSoldCost;
        const margin = totalSoldAmount > 0 ? Math.round((totalProfit / totalSoldAmount) * 1000) / 10 : 0;
        const totalTransactions = matchingSales.length;
        const distinctItemsCount = rows.length;

        const isExport = params.export === '1' || params.export === 'true';
        const page = Math.max(1, parseInt(params.page, 10) || 1);
        const limit = isExport ? 1000 : Math.max(1, parseInt(params.limit, 10) || 8);
        const total = rows.length;
        const totalPages = Math.ceil(total / limit) || 1;
        const paged = isExport ? rows : rows.slice((page - 1) * limit, page * limit);

        return {
          date_from: from,
          date_to: to,
          summary: {
            totalSoldQty,
            totalSoldAmount,
            totalSoldCost,
            totalProfit,
            margin,
            distinctItemsCount,
            totalTransactions,
          },
          items: paged,
          pagination: { page, limit, total, totalPages },
        };
      }
    }

    if (resource === 'medicines') {
      if (id) {
        const med = await db.medicines.get(id);
        if (!med) throw new Error('Medicine not found');
        return enrichMedicine(med);
      }
      const all = await db.medicines.toArray();
      let enriched = await Promise.all(all.map(enrichMedicine));
      if (params.search) {
        const q = String(params.search).toLowerCase();
        enriched = enriched.filter(
          (m) =>
            (m.name && m.name.toLowerCase().includes(q)) ||
            (m.generic_name && m.generic_name.toLowerCase().includes(q)) ||
            (m.barcode && m.barcode.toLowerCase().includes(q)) ||
            (m.sku && m.sku.toLowerCase().includes(q))
        );
      }
      const page = Math.max(1, parseInt(params.page, 10) || 1);
      const limit = Math.max(1, parseInt(params.limit, 10) || 50);
      const total = enriched.length;
      const pages = Math.ceil(total / limit) || 1;
      const paginated = enriched.slice((page - 1) * limit, page * limit);
      paginated.medicines = paginated;
      paginated.pagination = { total, page, limit, pages };
      return paginated;
    }

    if (resource === 'categories') {
      return db.categories.toArray();
    }

    if (resource === 'formulations') {
      const all = await db.formulations.toArray();
      return { formulations: all };
    }

    if (resource === 'customers') {
      if (id) {
        const cust = await db.customers.get(id);
        if (!cust) throw new Error('Customer not found');
        return cust;
      }
      const items = await applyDateFilter(db.customers, params);
      return items;
    }

    if (resource === 'suppliers') {
      if (id) {
        const sup = await db.suppliers.get(id);
        if (!sup) throw new Error('Supplier not found');
        return sup;
      }
      const items = await applyDateFilter(db.suppliers, params);
      return items;
    }

    if (resource === 'supplier-payments') {
      let all = await db.supplier_payments.toArray();
      if (params.supplier_id) {
        all = all.filter((p) => p.supplier_id === parseInt(params.supplier_id));
      }
      const enriched = await Promise.all(all.map(async (p) => {
        const supplier = p.supplier_id ? await db.suppliers.get(p.supplier_id) : null;
        return { ...p, supplier_name: supplier?.name || '-' };
      }));
      enriched.sort((a, b) => (b.payment_date || '').localeCompare(a.payment_date || ''));
      return enriched;
    }

    if (resource === 'sales') {
      if (parts[1] === 'staff-stats') {
        const users = db.users ? await db.users.toArray() : [];
        const sales = await db.sales.toArray();
        const from = params.date_from || '';
        const to = params.date_to || '';
        const search = (params.search || '').toLowerCase();
        const status = params.status || '';
        const paymentMethod = params.payment_method || '';

        const filteredSales = sales.filter((s) => {
          const d = (s.created_at || '').slice(0, 10);
          if (from && d < from) return false;
          if (to && d > to) return false;
          if (search && !(s.invoice_number || '').toLowerCase().includes(search)) return false;
          if (status && status !== 'all' && s.payment_status !== status) return false;
          if (paymentMethod && paymentMethod !== 'all' && s.payment_method !== paymentMethod) return false;
          return true;
        });

        const map = new Map();
        // 1. Populate all active users (including 0-sales staff)
        users.filter((u) => Number(u.is_active) === 1 || u.is_active === true || u.is_active === undefined).forEach((u) => {
          const id = Number(u.id);
          map.set(id, {
            staffId: id,
            staffName: u.full_name || u.username || 'Staff',
            username: u.username,
            role: (u.role || 'CASHIER').toUpperCase(),
            invoiceCount: 0,
            totalSales: 0,
            totalPaid: 0,
          });
        });

        // 2. Aggregate from filtered sales
        filteredSales.forEach((s) => {
          const uId = Number(s.user_id) || 1;
          if (!map.has(uId)) {
            map.set(uId, {
              staffId: uId,
              staffName: s.cashier_name || 'Admin',
              username: s.cashier_username || 'admin',
              role: (s.cashier_role || 'ADMIN').toUpperCase(),
              invoiceCount: 0,
              totalSales: 0,
              totalPaid: 0,
            });
          }
          const rec = map.get(uId);
          rec.invoiceCount += 1;
          rec.totalSales += Number(s.total || 0);
          rec.totalPaid += Number(s.paid_amount || 0);
        });

        const list = Array.from(map.values()).sort((a, b) => {
          if (a.role === 'ADMIN' && b.role !== 'ADMIN') return -1;
          if (b.role === 'ADMIN' && a.role !== 'ADMIN') return 1;
          if (b.totalSales !== a.totalSales) return b.totalSales - a.totalSales;
          if (b.invoiceCount !== a.invoiceCount) return b.invoiceCount - a.invoiceCount;
          return a.staffName.localeCompare(b.staffName);
        });

        return list;
      }

      const users = db.users ? await db.users.toArray() : [];
      const userMap = {};
      users.forEach((u) => { userMap[u.id] = u; });

      if (id) {
        const sale = await db.sales.get(id);
        if (!sale) throw new Error('Sale not found');
        const items = await db.sale_items.where('sale_id').equals(id).toArray();
        const enrichedItems = await Promise.all(items.map(async (item) => {
          const med = await db.medicines.get(item.medicine_id);
          return { ...item, medicine_name: med?.name || 'Unknown' };
        }));
        const customer = sale.customer_id ? await db.customers.get(sale.customer_id) : null;
        const staffUser = sale.user_id ? userMap[sale.user_id] : null;
        return {
          ...sale,
          customer_name: customer?.name || 'Walk-in Customer',
          cashier_name: staffUser ? (staffUser.full_name || staffUser.username || 'Admin') : (sale.cashier_name || 'Admin'),
          cashier_username: staffUser ? staffUser.username : (sale.cashier_username || 'admin'),
          cashier_role: staffUser ? (staffUser.role || 'admin') : (sale.cashier_role || 'admin'),
          items: enrichedItems,
        };
      }
      let all = await db.sales.toArray();
      const search = (params.search || '').toLowerCase();
      const status = params.status || '';
      if (params.customer_id !== undefined && params.customer_id !== '') {
        if (params.customer_id === 'walk-in' || params.customer_id === '0' || params.customer_id === 0) {
          all = all.filter((s) => !s.customer_id || s.customer_id === 0);
        } else {
          all = all.filter((s) => s.customer_id === parseInt(params.customer_id));
        }
      }
      if (search) {
        all = all.filter((s) => (s.invoice_number || '').toLowerCase().includes(search));
      }
      if (status && status !== 'all') {
        all = all.filter((s) => s.payment_status === status);
      }
      const enriched = await Promise.all(all.map(async (s) => {
        const customer = s.customer_id ? await db.customers.get(s.customer_id) : null;
        const staffUser = s.user_id ? userMap[s.user_id] : null;
        return {
          ...s,
          customer_name: customer?.name || 'Walk-in Customer',
          cashier_name: staffUser ? (staffUser.full_name || staffUser.username || 'Admin') : (s.cashier_name || 'Admin'),
          cashier_username: staffUser ? staffUser.username : (s.cashier_username || 'admin'),
          cashier_role: staffUser ? (staffUser.role || 'admin') : (s.cashier_role || 'admin'),
        };
      }));
      enriched.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      const limit = parseInt(params.limit) || 500;
      return enriched.slice(0, limit);
    }

    if (resource === 'purchases') {
      if (id) {
        const pur = await db.purchases.get(id);
        if (!pur) throw new Error('Purchase not found');
        const items = await db.purchase_items.where('purchase_id').equals(id).toArray();
        const enrichedItems = await Promise.all(items.map(async (item) => {
          const med = await db.medicines.get(item.medicine_id);
          return { ...item, medicine_name: med?.name || 'Unknown' };
        }));
        const supplier = pur.supplier_id ? await db.suppliers.get(pur.supplier_id) : null;
        return { ...pur, supplier_name: supplier?.name || 'General Supplier', items: enrichedItems };
      }
      let all = await db.purchases.toArray();
      if (params.supplier_id !== undefined && params.supplier_id !== '') {
        if (params.supplier_id === 'general' || params.supplier_id === 'walk-in' || params.supplier_id === '0' || params.supplier_id === 0) {
          all = all.filter((p) => !p.supplier_id || p.supplier_id === 0);
        } else {
          all = all.filter((p) => p.supplier_id === parseInt(params.supplier_id));
        }
      }
      const enriched = await Promise.all(all.map(async (p) => {
        const supplier = p.supplier_id ? await db.suppliers.get(p.supplier_id) : null;
        return { ...p, supplier_name: supplier?.name || 'General Supplier' };
      }));
      enriched.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      const limit = parseInt(params.limit) || 500;
      return enriched.slice(0, limit);
    }

    if (resource === 'supplier-refunds') {
      if (id) {
        const refund = await db.supplier_refunds.get(id);
        if (!refund) throw new Error('Supplier refund not found');
        const supplier = refund.supplier_id ? await db.suppliers.get(refund.supplier_id) : null;
        const purchase = refund.purchase_id ? await db.purchases.get(refund.purchase_id) : null;
        const rawItems = db.supplier_refund_items ? await db.supplier_refund_items.where('supplier_refund_id').equals(id).toArray() : [];
        let items = await Promise.all(rawItems.map(async (it) => {
          const med = it.medicine_id ? await db.medicines.get(it.medicine_id) : null;
          return {
            ...it,
            medicine_name: med?.name || ('Medicine #' + it.medicine_id),
            medicine_unit: med?.unit || 'pcs',
            medicine_generic_name: med?.generic_name || null,
          };
        }));
        if (items.length === 0 && refund.medicine_id) {
          const med = await db.medicines.get(refund.medicine_id);
          items = [{
            id: null,
            supplier_refund_id: id,
            medicine_id: refund.medicine_id,
            medicine_name: med?.name || ('Medicine #' + refund.medicine_id),
            medicine_unit: med?.unit || 'pcs',
            medicine_generic_name: med?.generic_name || null,
            unit_name: refund.unit_name || null,
            unit_quantity: refund.unit_quantity !== null ? parseInt(refund.unit_quantity) : 1,
            conversion_factor: parseInt(refund.conversion_factor) || 1,
            return_quantity: parseInt(refund.return_quantity) || 1,
            unit_price: 0,
            refund_amount: parseFloat(refund.refund_amount) || 0,
          }];
        }
        return {
          refund: {
            ...refund,
            supplier_name: supplier?.name || 'General Supplier',
            supplier_phone: supplier?.phone || null,
            supplier_address: supplier?.address || null,
            supplier_balance: supplier?.balance || 0,
            purchase_reference: purchase?.reference_number || (refund.purchase_id ? `PUR-${refund.purchase_id}` : '-'),
            purchase_total: purchase?.total || 0,
            purchase_paid_amount: purchase?.paid_amount || 0,
            items,
            items_count: items.length,
          }
        };
      }
      let all = await db.supplier_refunds.toArray();
      if (params.supplier_id !== undefined && params.supplier_id !== '') {
        if (params.supplier_id === 'general' || params.supplier_id === 'walk-in' || params.supplier_id === '0' || params.supplier_id === 0) {
          all = all.filter((r) => !r.supplier_id || r.supplier_id === 0);
        } else {
          all = all.filter((r) => r.supplier_id === parseInt(params.supplier_id));
        }
      }
      if (params.purchase_id) {
        all = all.filter((r) => r.purchase_id === parseInt(params.purchase_id));
      }
      const enriched = await Promise.all(all.map(async (r) => {
        const supplier = r.supplier_id ? await db.suppliers.get(r.supplier_id) : null;
        const purchase = r.purchase_id ? await db.purchases.get(r.purchase_id) : null;
        const rawItems = db.supplier_refund_items ? await db.supplier_refund_items.where('supplier_refund_id').equals(r.id).toArray() : [];
        let items = await Promise.all(rawItems.map(async (it) => {
          const med = it.medicine_id ? await db.medicines.get(it.medicine_id) : null;
          return {
            ...it,
            medicine_name: med?.name || ('Medicine #' + it.medicine_id),
            medicine_unit: med?.unit || 'pcs',
          };
        }));
        if (items.length === 0 && r.medicine_id) {
          const med = await db.medicines.get(r.medicine_id);
          items = [{
            medicine_id: r.medicine_id,
            medicine_name: med?.name || ('Medicine #' + r.medicine_id),
            medicine_unit: med?.unit || 'pcs',
            unit_name: r.unit_name || null,
            unit_quantity: r.unit_quantity !== null ? parseInt(r.unit_quantity) : 1,
            conversion_factor: parseInt(r.conversion_factor) || 1,
            return_quantity: parseInt(r.return_quantity) || 1,
            refund_amount: parseFloat(r.refund_amount) || 0,
          }];
        }
        const itemNames = items.map((it) => {
          const qtyDisplay = it.unit_name ? `${it.unit_quantity} ${it.unit_name}` : `${it.return_quantity} ${it.medicine_unit || 'units'}`;
          return `${it.medicine_name} (${qtyDisplay})`;
        });
        return {
          ...r,
          supplier_name: supplier?.name || 'General Supplier',
          purchase_reference: purchase?.reference_number || (r.purchase_id ? `PUR-${r.purchase_id}` : '-'),
          items,
          items_count: items.length,
          items_summary: itemNames.join(', '),
          medicine_name: items[0]?.medicine_name || '-',
          medicine_unit: items[0]?.medicine_unit || '',
        };
      }));
      enriched.sort((a, b) => (b.refund_date || b.created_at || '').localeCompare(a.refund_date || a.created_at || ''));
      return enriched;
    }

    if (resource === 'customer-refunds') {
      if (id) {
        const refund = await db.customer_refunds.get(id);
        if (!refund) throw new Error('Customer refund not found');
        const customer = refund.customer_id ? await db.customers.get(refund.customer_id) : null;
        const sale = refund.sale_id ? await db.sales.get(refund.sale_id) : null;
        const rawItems = db.customer_refund_items ? await db.customer_refund_items.where('customer_refund_id').equals(id).toArray() : [];
        let items = await Promise.all(rawItems.map(async (it) => {
          const med = it.medicine_id ? await db.medicines.get(it.medicine_id) : null;
          return {
            ...it,
            medicine_name: med?.name || ('Medicine #' + it.medicine_id),
            medicine_unit: med?.unit || 'pcs',
            medicine_generic_name: med?.generic_name || null,
          };
        }));
        if (items.length === 0 && refund.medicine_id) {
          const med = await db.medicines.get(refund.medicine_id);
          items = [{
            id: null,
            customer_refund_id: id,
            medicine_id: refund.medicine_id,
            medicine_name: med?.name || ('Medicine #' + refund.medicine_id),
            medicine_unit: med?.unit || 'pcs',
            medicine_generic_name: med?.generic_name || null,
            unit_name: refund.unit_name || null,
            unit_quantity: refund.unit_quantity !== null ? parseInt(refund.unit_quantity) : 1,
            conversion_factor: parseInt(refund.conversion_factor) || 1,
            return_quantity: parseInt(refund.return_quantity) || 1,
            unit_price: 0,
            refund_amount: parseFloat(refund.refund_amount) || 0,
          }];
        }
        return {
          refund: {
            ...refund,
            customer_name: customer?.name || 'Walk-in Customer',
            customer_phone: customer?.phone || null,
            customer_balance: customer?.balance || 0,
            sale_invoice_number: sale?.invoice_number || (refund.sale_id ? `INV-${refund.sale_id}` : '-'),
            sale_total: sale?.total || 0,
            sale_paid_amount: sale?.paid_amount || 0,
            items,
            items_count: items.length,
          }
        };
      }
      let all = await db.customer_refunds.toArray();
      if (params.customer_id !== undefined && params.customer_id !== '') {
        if (params.customer_id === 'walk-in' || params.customer_id === '0' || params.customer_id === 0) {
          all = all.filter((r) => !r.customer_id || r.customer_id === 0);
        } else {
          all = all.filter((r) => r.customer_id === parseInt(params.customer_id));
        }
      }
      if (params.sale_id) {
        all = all.filter((r) => r.sale_id === parseInt(params.sale_id));
      }
      const enriched = await Promise.all(all.map(async (r) => {
        const customer = r.customer_id ? await db.customers.get(r.customer_id) : null;
        const sale = r.sale_id ? await db.sales.get(r.sale_id) : null;
        const rawItems = db.customer_refund_items ? await db.customer_refund_items.where('customer_refund_id').equals(r.id).toArray() : [];
        let items = await Promise.all(rawItems.map(async (it) => {
          const med = it.medicine_id ? await db.medicines.get(it.medicine_id) : null;
          return {
            ...it,
            medicine_name: med?.name || ('Medicine #' + it.medicine_id),
            medicine_unit: med?.unit || 'pcs',
          };
        }));
        if (items.length === 0 && r.medicine_id) {
          const med = await db.medicines.get(r.medicine_id);
          items = [{
            medicine_id: r.medicine_id,
            medicine_name: med?.name || ('Medicine #' + r.medicine_id),
            medicine_unit: med?.unit || 'pcs',
            unit_name: r.unit_name || null,
            unit_quantity: r.unit_quantity !== null ? parseInt(r.unit_quantity) : 1,
            conversion_factor: parseInt(r.conversion_factor) || 1,
            return_quantity: parseInt(r.return_quantity) || 1,
            refund_amount: parseFloat(r.refund_amount) || 0,
          }];
        }
        const itemNames = items.map((it) => {
          const qtyDisplay = it.unit_name ? `${it.unit_quantity} ${it.unit_name}` : `${it.return_quantity} ${it.medicine_unit || 'units'}`;
          return `${it.medicine_name} (${qtyDisplay})`;
        });
        return {
          ...r,
          customer_name: customer?.name || 'Walk-in Customer',
          sale_invoice_number: sale?.invoice_number || (r.sale_id ? `INV-${r.sale_id}` : '-'),
          items,
          items_count: items.length,
          items_summary: itemNames.join(', '),
          medicine_name: items[0]?.medicine_name || '-',
          medicine_unit: items[0]?.medicine_unit || '',
        };
      }));
      enriched.sort((a, b) => (b.refund_date || b.created_at || '').localeCompare(a.refund_date || a.created_at || ''));
      return enriched;
    }

    if (resource === 'customer-payments') {
      let all = await db.customer_payments.toArray();
      if (params.customer_id) {
        all = all.filter((p) => p.customer_id === parseInt(params.customer_id));
      }
      const enriched = await Promise.all(all.map(async (p) => {
        const customer = p.customer_id ? await db.customers.get(p.customer_id) : null;
        return { ...p, customer_name: customer?.name || '-' };
      }));
      enriched.sort((a, b) => (b.payment_date || '').localeCompare(a.payment_date || ''));
      return enriched;
    }

    if (resource === 'damages') {
      let all = db.damages ? await db.damages.toArray() : [];
      if (params.date_from) {
        all = all.filter((d) => (d.damage_date || d.created_at || '') >= params.date_from);
      }
      if (params.date_to) {
        all = all.filter((d) => (d.damage_date || d.created_at || '') <= params.date_to);
      }
      const enriched = await Promise.all(all.map(async (d) => {
        const medicine = d.medicine_id ? await db.medicines.get(d.medicine_id) : null;
        const batch = d.batch_id ? await db.batches.get(d.batch_id) : null;
        return {
          ...d,
          medicine_name: medicine?.name || '-',
          generic_name: medicine?.generic_name || '',
          unit: medicine?.unit || 'pcs',
          unit_name: d.unit_name || medicine?.unit || 'pcs',
          unit_quantity: d.unit_quantity != null ? d.unit_quantity : d.quantity,
          conversion_factor: d.conversion_factor || 1,
          batch_number: batch?.batch_number || '-',
          expiry_date: batch?.expiry_date || null,
        };
      }));
      enriched.sort((a, b) => (b.damage_date || '').localeCompare(a.damage_date || '') || (b.id || 0) - (a.id || 0));
      const limit = parseInt(params.limit) || 500;
      return enriched.slice(0, limit);
    }

    if (resource === 'supplier-payments') {
      let all = db.supplier_payments ? await db.supplier_payments.toArray() : [];
      if (params.supplier_id) {
        all = all.filter((p) => p.supplier_id === parseInt(params.supplier_id));
      }
      const enriched = await Promise.all(all.map(async (p) => {
        const supplier = p.supplier_id ? await db.suppliers.get(p.supplier_id) : null;
        return { ...p, supplier_name: supplier?.name || '-' };
      }));
      enriched.sort((a, b) => (b.payment_date || '').localeCompare(a.payment_date || ''));
      return { payments: enriched };
    }

    if (resource === 'expenses') {
      const items = (await applyDateFilter(db.expenses, params)).filter((e) => e.category !== 'Advance Payment');
      const types = await db.expense_types.toArray();
      const typeMap = {};
      types.forEach((t) => { typeMap[t.id] = t; });
      return items.map((e) => ({
        ...e,
        expense_type_name: e.expense_type_id ? (typeMap[e.expense_type_id]?.name || null) : null,
        expense_type_color: e.expense_type_id ? (typeMap[e.expense_type_id]?.color || null) : null,
      }));
    }

    if (resource === 'expense-types') {
      let types = await db.expense_types.toArray();
      if (types.length === 0) {
        const defaults = [
          { name: 'Rent', color: 'blue', is_active: 1, sort_order: 1 },
          { name: 'Utilities', color: 'purple', is_active: 1, sort_order: 2 },
          { name: 'Salaries', color: 'emerald', is_active: 1, sort_order: 3 },
          { name: 'Advance Payment', color: 'amber', is_active: 1, sort_order: 4 },
          { name: 'Transport', color: 'amber', is_active: 1, sort_order: 5 },
          { name: 'Marketing', color: 'pink', is_active: 1, sort_order: 6 },
          { name: 'Maintenance', color: 'orange', is_active: 1, sort_order: 7 },
          { name: 'Insurance', color: 'cyan', is_active: 1, sort_order: 8 },
          { name: 'Other', color: 'gray', is_active: 1, sort_order: 9 },
        ];
        for (const t of defaults) {
          await db.expense_types.add({ ...t, created_at: new Date().toISOString(), updated_at: new Date().toISOString() });
        }
        types = await db.expense_types.toArray();
      }
      return types;
    }

    if (resource === 'staff') {
      const items = await applyDateFilter(db.staff, params);
      return items;
    }

    if (resource === 'staff-transactions') {
      const all = db.staff_transactions ? await db.staff_transactions.toArray() : [];
      const staffList = db.staff ? await db.staff.toArray() : [];
      const staffMap = {};
      staffList.forEach((s) => { staffMap[s.id] = s; });

      let filtered = all;
      if (params.staff_id) {
        filtered = filtered.filter((t) => Number(t.staff_id) === Number(params.staff_id));
      }
      if (params.type && params.type !== 'all') {
        filtered = filtered.filter((t) => t.type === params.type);
      }
      if (params.date_from) {
        filtered = filtered.filter((t) => (t.transaction_date || (t.created_at || '').slice(0, 10)) >= params.date_from);
      }
      if (params.date_to) {
        filtered = filtered.filter((t) => (t.transaction_date || (t.created_at || '').slice(0, 10)) <= params.date_to);
      }

      filtered.sort((a, b) => {
        const t1 = new Date(b.transaction_date || b.created_at || 0).getTime();
        const t2 = new Date(a.transaction_date || a.created_at || 0).getTime();
        return t1 - t2;
      });

      const enriched = filtered.map((st) => {
        const s = staffMap[st.staff_id] || {};
        return {
          ...st,
          staff_name: s.name || 'Staff',
          staff_phone: s.phone || '',
          staff_base_salary: s.salary_amount || 0,
          current_staff_advance: s.advanced_payment || 0,
        };
      });

      const limit = parseInt(params.limit, 10) || 500;
      return { transactions: enriched.slice(0, limit) };
    }

    if (resource === 'users') {
      const users = db.users ? await db.users.toArray() : [];
      if (params.search) {
        const q = String(params.search).toLowerCase();
        const filtered = users.filter((u) =>
          (u.full_name && u.full_name.toLowerCase().includes(q)) ||
          (u.username && u.username.toLowerCase().includes(q)) ||
          (u.email && u.email.toLowerCase().includes(q))
        );
        return {
          users: filtered,
          pagination: { total: filtered.length, page: 1, limit: filtered.length || 50, pages: 1 }
        };
      }
      return {
        users,
        pagination: { total: users.length, page: 1, limit: users.length || 50, pages: 1 }
      };
    }

    if (resource === 'batches') {
      if (params.medicine_id) {
        return db.batches.where('medicine_id').equals(parseInt(params.medicine_id)).toArray();
      }
      return db.batches.toArray();
    }

    if (resource === 'settings') {
      return getSettings();
    }

    throw new Error(`GET ${endpoint} not supported in offline mode`);
  },

  post: async (endpoint, data) => {
    const { parts, id } = parseEndpoint(endpoint);
    const resource = parts[0];

    if (resource === 'pos' && parts[1] === 'checkout') {
      return processCheckout(data);
    }

    if (resource === 'auth' && parts[1] === 'login') {
      throw new Error('Login requires a connection to the server');
    }

    if (resource === 'ai') {
      if (parts[1] === 'test') {
        const settings = getSettings();
        const apiKey = (data?.gemini_api_key || settings.gemini_api_key || '').trim();
        const model = (data?.gemini_model || settings.gemini_model || 'gemini-1.5-flash').trim();

        if (!apiKey) {
          throw new Error('No API key provided. Please enter a valid Google Gemini API key.');
        }

        const result = await callGeminiApi(
          apiKey,
          model,
          'You are a helpful pharmacy assistant. Respond briefly to verify connectivity.',
          [],
          'Hello, respond with "Connected successfully!"'
        );

        return {
          message: `Connected successfully! Gemini (${result.model}) responded: ${result.reply.trim()}`,
          model: result.model,
        };
      }

      if (parts[1] === 'chat') {
        const settings = getSettings();
        const apiKey = (settings.gemini_api_key || '').trim();
        const model = (settings.gemini_model || 'gemini-1.5-flash').trim();

        if (!apiKey) {
          throw new Error('Gemini API key is not configured. Please go to Settings to add your Google Gemini API key.');
        }

        const userMessage = data?.message || '';
        if (!userMessage.trim()) {
          throw new Error('Message is required');
        }

        const pharmacyName = settings.pharmacy_name || 'Gaza Pharmacy';
        const currency = settings.currency || 'Rs.';
        const expiryDays = parseInt(settings.expiry_alert_days || '90', 10);
        const lowStockThreshold = parseInt(settings.low_stock_threshold || '10', 10);

        const [searchedMeds, lowStock, expiryBatches, salesData, topSellers, finances, categories] = await Promise.all([
          searchMedicinesContext(userMessage, currency),
          getLowStockContext(lowStockThreshold),
          getExpiryContext(expiryDays),
          getSalesContext(),
          getTopSellersContext(),
          getFinancialContext(),
          getCategoriesContext(),
        ]);

        const systemInstruction = `You are the dedicated, professional AI Pharmacy Assistant for "${pharmacyName}".
Current System Date & Time: ${new Date().toLocaleString()}
Primary Currency: ${currency}

STRICT DOMAIN SCOPE & BOUNDARY RULES:
1. You are EXCLUSIVELY an AI Pharmacy & Healthcare Operations Assistant.
2. NEVER answer off-topic queries, general math (e.g. "what is 2+2"), coding, politics, or non-pharmacy trivia. If asked, politely decline and ask for a pharmacy-related question.
3. MEDICINE AVAILABILITY & STOCK QUERIES (CRITICAL RULE):
   - When asked if a medicine is in stock or available (e.g., "do we have [Medicine]?", "is [Medicine] in stock?"):
     a) Check the [MATCHED INVENTORY RESULTS] below.
     b) If found with stock > 0: State that it is available, along with the exact quantity in stock and price.
     c) If found with stock = 0: State that it is currently out of stock (0 units).
     d) If NOT found in the database / inventory: Simply state directly and concisely that it is not available in the pharmacy (e.g., "No, [Medicine] is not available in our pharmacy inventory.").
     e) DO NOT output unwanted extra medicine descriptions, general uses, or pharmacology essays when the user only asked if the medicine is available. Keep availability responses short, clear, and direct.
4. Only provide therapeutic uses, dosage guidance, or alternatives when the user specifically asks for them (e.g. "what is panadol used for?", "what alternatives do we have for X?").

REAL-TIME PHARMACY DATABASE SNAPSHOT:
========================================
[MATCHED INVENTORY RESULTS FOR USER QUERY]
${searchedMeds}

[INVENTORY & STOCK STATUS]
• Low Stock Medicines (At or below reorder level):
${lowStock}

• Expiring Batches (Expiring within ${expiryDays} days):
${expiryBatches}

[SALES & REVENUE SNAPSHOT]
• Today's Sales: ${salesData.today.orders} orders, Total Revenue: ${currency} ${salesData.today.total}
• This Month's Sales: ${salesData.month.orders} orders, Total Revenue: ${currency} ${salesData.month.total}
• Top Selling Medicines This Month:
${topSellers}

[FINANCIAL BALANCES]
• Customer Receivables (Total Unpaid Credit Balance): ${currency} ${finances.receivables}
• Supplier Payables (Total Unpaid Balance): ${currency} ${finances.payables}

[PRODUCT CATEGORIES IN PHARMACY]
${categories}
========================================

Always base your answers on the real data above. Be direct and concise.`;

        const result = await callGeminiApi(
          apiKey,
          model,
          systemInstruction,
          data?.history || [],
          userMessage
        );

        return {
          reply: result.reply,
          model: result.model,
        };
      }
    }

    if (resource === 'settings') {
      saveSettings(data);
      return data;
    }

    if (resource === 'medicines') {
      const id = await db.medicines.add({
        ...data,
        created_at: data.created_at || new Date().toISOString(),
        updated_at: data.updated_at || new Date().toISOString(),
      });
      return { id };
    }

    if (resource === 'categories') {
      const id = await db.categories.add(data);
      return { id };
    }

    if (resource === 'formulations') {
      const id = await db.formulations.add(data);
      return { id, name: data.name };
    }

    if (resource === 'customers') {
      const pendingAmount = Math.max(0, parseFloat(data.pending_amount ?? data.balance) || 0);
      const advanceAmount = Math.max(0, parseFloat(data.advance_amount) || 0);
      const paymentMethod = ['cash', 'card', 'online', 'bank'].includes(data.advance_payment_method) ? data.advance_payment_method : 'cash';
      const { pending_amount, advance_amount, advance_payment_method, ...customerData } = data;
      const id = await db.customers.add({ ...customerData, balance: pendingAmount - advanceAmount, created_at: new Date().toISOString() });
      if (advanceAmount > 0) await db.customer_payments.add({ customer_id: id, payment_date: new Date().toISOString().slice(0, 10), payment_method: paymentMethod, payment_type: 'received', amount: advanceAmount, notes: `Opening advance received${data.notes ? `: ${data.notes}` : ''}`, user_id: data.user_id || 1, created_at: new Date().toISOString() });
      return { id };
    }

    if (resource === 'suppliers') {
      const pendingAmount = Math.max(0, parseFloat(data.pending_amount ?? data.balance) || 0);
      const advanceAmount = Math.max(0, parseFloat(data.advance_amount) || 0);
      const paymentMethod = ['cash', 'card', 'online', 'bank'].includes(data.advance_payment_method) ? data.advance_payment_method : 'cash';
      const { pending_amount, advance_amount, advance_payment_method, ...supplierData } = data;
      const id = await db.suppliers.add({
        ...supplierData,
        balance: pendingAmount,
        credit_balance: (parseFloat(data.credit_balance) || 0) + advanceAmount,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      if (advanceAmount > 0) {
        await db.supplier_payments.add({
          supplier_id: id,
          payment_date: new Date().toISOString().slice(0, 10),
          payment_method: paymentMethod,
          payment_type: 'paid',
          amount: advanceAmount,
          notes: `Initial advance payment${data.notes ? `: ${data.notes}` : ''}`,
          user_id: data.user_id || 1,
          created_at: new Date().toISOString(),
        });
      }
      return { id };
    }

    if (resource === 'batches') {
      const id = await db.batches.add(data);
      return { id };
    }

    if (resource === 'damages') {
      const batch = await db.batches.get(parseInt(data.batch_id));
      if (!batch) throw new Error('Batch not found');
      const unitQty = parseInt(data.unit_quantity != null ? data.unit_quantity : data.quantity) || 0;
      const conversionFactor = Math.max(1, parseInt(data.conversion_factor) || 1);
      const baseQty = unitQty * conversionFactor;
      if (unitQty <= 0) throw new Error('Quantity must be greater than 0');
      if ((Number(batch.quantity) || 0) < baseQty) {
        throw new Error('Damaged quantity cannot be greater than available batch stock');
      }
      const id = await db.damages.add({
        medicine_id: parseInt(data.medicine_id),
        batch_id: parseInt(data.batch_id),
        damage_date: data.damage_date || new Date().toISOString().slice(0, 10),
        quantity: baseQty,
        unit_name: data.unit_name || null,
        unit_quantity: unitQty,
        conversion_factor: conversionFactor,
        reason: data.reason || 'Damaged',
        notes: data.notes || '',
        user_id: data.user_id || 1,
        created_at: new Date().toISOString(),
      });
      await db.batches.update(batch.id, { quantity: (Number(batch.quantity) || 0) - baseQty });
      return { id };
    }

    if (resource === 'purchases') {
      const purchaseId = await db.purchases.add({
        reference_number: data.reference_number,
        supplier_id: data.supplier_id ? parseInt(data.supplier_id) : null,
        user_id: data.user_id ? parseInt(data.user_id) : 1,
        purchase_date: data.purchase_date || new Date().toISOString().slice(0, 10),
        payment_method: data.payment_method || 'cash',
        subtotal: Number(data.subtotal) || 0,
        discount: Number(data.discount) || 0,
        tax: Number(data.tax) || 0,
        total: Number(data.total) || 0,
        payment_status: data.payment_status || 'paid',
        paid_amount: Number(data.paid_amount) || 0,
        checkout_paid_amount: Number(data.paid_amount) || 0,
        balance_due: Number(data.balance_due) || 0,
        notes: data.notes || null,
        created_at: new Date().toISOString(),
      });

      for (const item of (data.items || [])) {
        const uQty = parseInt(item.unit_quantity || item.quantity) || 1;
        const conv = parseInt(item.conversion_factor) || 1;
        const baseQty = uQty * conv;
        const buyPrice = parseFloat(item.buy_price || item.unit_price) || 0;
        const sellPrice = parseFloat(item.sell_price) || 0;
        const itemDisc = parseFloat(item.discount) || 0;
        const lineGross = buyPrice * uQty;
        const discAmt = (lineGross * itemDisc) / 100;
        const lineTot = item.total !== undefined ? Number(item.total) : (lineGross - discAmt);
        const bNo = item.batch_number || `BN-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${item.medicine_id}`;

        await db.purchase_items.add({
          purchase_id: purchaseId,
          medicine_id: parseInt(item.medicine_id),
          batch_number: bNo,
          mf_date: item.mf_date || null,
          expiry_date: item.expiry_date || null,
          unit_name: item.unit_name || null,
          unit_quantity: uQty,
          conversion_factor: conv,
          quantity: baseQty,
          buy_price: buyPrice,
          sell_price: sellPrice,
          discount: itemDisc,
          total: lineTot,
        });

        const existingBatches = await db.batches.where('medicine_id').equals(parseInt(item.medicine_id)).toArray();
        const matchBatch = existingBatches.find((b) => b.batch_number === bNo);
        if (matchBatch) {
          await db.batches.update(matchBatch.id, {
            quantity: (Number(matchBatch.quantity) || 0) + baseQty,
            cost_price: buyPrice > 0 ? (buyPrice / conv) : matchBatch.cost_price,
            selling_price: sellPrice > 0 ? (sellPrice / conv) : matchBatch.selling_price,
            mf_date: item.mf_date || matchBatch.mf_date,
            expiry_date: item.expiry_date || matchBatch.expiry_date,
          });
        } else {
          await db.batches.add({
            medicine_id: parseInt(item.medicine_id),
            batch_number: bNo,
            quantity: baseQty,
            cost_price: buyPrice > 0 ? (buyPrice / conv) : 0,
            selling_price: sellPrice > 0 ? (sellPrice / conv) : 0,
            mf_date: item.mf_date || null,
            expiry_date: item.expiry_date || null,
            created_at: new Date().toISOString(),
          });
        }
      }

      if (data.supplier_id && (Number(data.balance_due) || 0) > 0) {
        const sup = await db.suppliers.get(parseInt(data.supplier_id));
        if (sup) {
          await db.suppliers.update(parseInt(data.supplier_id), {
            balance: (Number(sup.balance) || 0) + Number(data.balance_due),
          });
        }
      }

      return { id: purchaseId };
    }

    if (resource === 'supplier-refunds') {
      const normalizedSupplierId = (data.supplier_id === 'general' || data.supplier_id === 'walk-in' || data.supplier_id === '0' || !data.supplier_id) ? null : parseInt(data.supplier_id);
      const rawItems = Array.isArray(data.items) ? data.items : [];
      let totalAmount = parseFloat(data.refund_amount) || 0;
      if (totalAmount <= 0 && rawItems.length > 0) {
        totalAmount = rawItems.reduce((s, it) => s + (parseFloat(it.refund_amount) || 0), 0);
      }

      const firstItem = rawItems[0] || {};
      const id = await db.supplier_refunds.add({
        supplier_id: normalizedSupplierId,
        purchase_id: data.purchase_id ? parseInt(data.purchase_id) : null,
        refund_date: data.refund_date || new Date().toISOString().slice(0, 10),
        refund_type: data.refund_type || 'return',
        refund_amount: totalAmount,
        medicine_id: rawItems.length === 1 ? (parseInt(firstItem.medicine_id) || null) : (data.medicine_id ? parseInt(data.medicine_id) : null),
        unit_name: rawItems.length === 1 ? (firstItem.unit_name || null) : (data.unit_name || null),
        unit_quantity: rawItems.length === 1 ? (parseInt(firstItem.unit_quantity) || 1) : (data.unit_quantity ? parseInt(data.unit_quantity) : null),
        conversion_factor: rawItems.length === 1 ? (parseInt(firstItem.conversion_factor) || 1) : (data.conversion_factor ? parseInt(data.conversion_factor) : 1),
        return_quantity: rawItems.length === 1 ? (parseInt(firstItem.return_quantity) || 1) : (data.return_quantity ? parseInt(data.return_quantity) : null),
        reason: data.reason || '',
        notes: data.notes || '',
        user_id: data.user_id ? parseInt(data.user_id) : 1,
        created_at: new Date().toISOString(),
      });

      if (data.refund_type === 'return') {
        const itemsToProcess = rawItems.length > 0 ? rawItems : (data.medicine_id ? [{
          medicine_id: data.medicine_id,
          unit_name: data.unit_name,
          unit_quantity: data.unit_quantity,
          conversion_factor: data.conversion_factor,
          return_quantity: data.return_quantity,
          unit_price: data.unit_price,
          refund_amount: data.refund_amount,
        }] : []);

        for (const it of itemsToProcess) {
          const mId = parseInt(it.medicine_id);
          const uQty = parseInt(it.unit_quantity) || 1;
          const conv = parseInt(it.conversion_factor) || 1;
          const retQty = it.return_quantity !== undefined && it.return_quantity !== null ? parseInt(it.return_quantity) : (uQty * conv);
          const uPrice = parseFloat(it.unit_price) || 0;
          const rAmt = parseFloat(it.refund_amount) || (uQty * uPrice);

          if (db.supplier_refund_items) {
            await db.supplier_refund_items.add({
              supplier_refund_id: id,
              medicine_id: mId,
              unit_name: it.unit_name || null,
              unit_quantity: uQty,
              conversion_factor: conv,
              return_quantity: retQty,
              unit_price: uPrice,
              refund_amount: rAmt,
              created_at: new Date().toISOString(),
            });
          }

          if (mId && retQty > 0) {
            const batches = await db.batches.where('medicine_id').equals(mId).toArray();
            const sorted = batches.sort((a, b) => (a.expiry_date || '').localeCompare(b.expiry_date || ''));
            let remaining = retQty;
            for (const batch of sorted) {
              if (remaining <= 0) break;
              const deduct = Math.min(batch.quantity, remaining);
              await db.batches.update(batch.id, { quantity: batch.quantity - deduct });
              remaining -= deduct;
            }
          }
        }
      }

      if (normalizedSupplierId && normalizedSupplierId > 0 && totalAmount > 0) {
        const supplier = await db.suppliers.get(normalizedSupplierId);
        if (supplier) {
          const currentBal = Number(supplier.balance || 0);
          await db.suppliers.update(normalizedSupplierId, { balance: currentBal - totalAmount });
        }
      }

      return { id };
    }

    if (resource === 'customer-refunds') {
      const normalizedCustomerId = (data.customer_id === 'walk-in' || data.customer_id === '0' || !data.customer_id) ? null : parseInt(data.customer_id);
      const rawItems = Array.isArray(data.items) ? data.items : [];
      let totalAmount = parseFloat(data.refund_amount) || 0;
      if (totalAmount <= 0 && rawItems.length > 0) {
        totalAmount = rawItems.reduce((s, it) => s + (parseFloat(it.refund_amount) || 0), 0);
      }

      const firstItem = rawItems[0] || {};
      const id = await db.customer_refunds.add({
        customer_id: normalizedCustomerId,
        sale_id: data.sale_id ? parseInt(data.sale_id) : null,
        refund_date: data.refund_date || new Date().toISOString().slice(0, 10),
        refund_type: data.refund_type || 'return',
        refund_amount: totalAmount,
        medicine_id: rawItems.length === 1 ? (parseInt(firstItem.medicine_id) || null) : (data.medicine_id ? parseInt(data.medicine_id) : null),
        unit_name: rawItems.length === 1 ? (firstItem.unit_name || null) : (data.unit_name || null),
        unit_quantity: rawItems.length === 1 ? (parseInt(firstItem.unit_quantity) || 1) : (data.unit_quantity ? parseInt(data.unit_quantity) : null),
        conversion_factor: rawItems.length === 1 ? (parseInt(firstItem.conversion_factor) || 1) : (data.conversion_factor ? parseInt(data.conversion_factor) : 1),
        return_quantity: rawItems.length === 1 ? (parseInt(firstItem.return_quantity) || 1) : (data.return_quantity ? parseInt(data.return_quantity) : null),
        reason: data.reason || '',
        notes: data.notes || '',
        user_id: data.user_id ? parseInt(data.user_id) : 1,
        created_at: new Date().toISOString(),
      });

      if (data.refund_type === 'return') {
        const itemsToProcess = rawItems.length > 0 ? rawItems : (data.medicine_id ? [{
          medicine_id: data.medicine_id,
          unit_name: data.unit_name,
          unit_quantity: data.unit_quantity,
          conversion_factor: data.conversion_factor,
          return_quantity: data.return_quantity,
          unit_price: data.unit_price,
          refund_amount: data.refund_amount,
        }] : []);

        for (const it of itemsToProcess) {
          const mId = parseInt(it.medicine_id);
          const uQty = parseInt(it.unit_quantity) || 1;
          const conv = parseInt(it.conversion_factor) || 1;
          const retQty = it.return_quantity !== undefined && it.return_quantity !== null ? parseInt(it.return_quantity) : (uQty * conv);
          const uPrice = parseFloat(it.unit_price) || 0;
          const rAmt = parseFloat(it.refund_amount) || (uQty * uPrice);

          if (db.customer_refund_items) {
            await db.customer_refund_items.add({
              customer_refund_id: id,
              medicine_id: mId,
              unit_name: it.unit_name || null,
              unit_quantity: uQty,
              conversion_factor: conv,
              return_quantity: retQty,
              unit_price: uPrice,
              refund_amount: rAmt,
              created_at: new Date().toISOString(),
            });
          }

          if (mId && retQty > 0) {
            const batches = await db.batches.where('medicine_id').equals(mId).toArray();
            const sorted = batches.sort((a, b) => (b.id || 0) - (a.id || 0));
            if (sorted.length > 0) {
              await db.batches.update(sorted[0].id, { quantity: sorted[0].quantity + retQty });
            }
          }
        }
      }

      if (normalizedCustomerId && normalizedCustomerId > 0 && totalAmount > 0) {
        const customer = await db.customers.get(normalizedCustomerId);
        if (customer) {
          const currentBal = Number(customer.balance || 0);
          await db.customers.update(normalizedCustomerId, { balance: currentBal - totalAmount });
        }
      }

      return { id };
    }

    if (resource === 'customer-payments') {
      const isRefund = data.payment_type === 'refund';
      const id = await db.customer_payments.add({
        customer_id: data.customer_id,
        payment_date: data.payment_date || new Date().toISOString().slice(0, 10),
        payment_method: data.payment_method || 'cash',
        payment_type: data.payment_type || 'received',
        amount: parseFloat(data.amount) || 0,
        sale_id: data.sale_id ? Number(data.sale_id) : null,
        client_request_id: data.client_request_id || null,
        notes: data.notes || '',
        user_id: data.user_id || 1,
        created_at: new Date().toISOString(),
      });

      if (data.customer_id && data.amount) {
        const customer = await db.customers.get(data.customer_id);
        if (customer) {
          const currentBal = Number(customer.balance || 0);
          const paidAmount = parseFloat(data.amount) || 0;
          const newBal = isRefund ? currentBal + paidAmount : currentBal - paidAmount;
          await db.customers.update(data.customer_id, { balance: newBal });
        }

        if (!isRefund) {
          // Allocate payment to unpaid/partial sales of this customer in FIFO order
          let remaining = parseFloat(data.amount) || 0;
          const allocations = [];
          if (remaining > 0) {
            let customerSales = await db.sales.where('customer_id').equals(data.customer_id).toArray();
            if (data.sale_id) customerSales = customerSales.filter((sale) => Number(sale.id) === Number(data.sale_id));
            customerSales.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
            for (const s of customerSales) {
              if (remaining <= 0) break;
              const saleTotal = parseFloat(s.total) || 0;
              const salePaid = parseFloat(s.paid_amount) || 0;
              const due = Math.max(0, saleTotal - salePaid);
              if (due > 0) {
                const apply = Math.min(due, remaining);
                const newPaid = salePaid + apply;
                const newStatus = newPaid >= saleTotal - 0.001 ? 'paid' : 'partial';
                await db.sales.update(s.id, {
                  paid_amount: newPaid,
                  payment_status: newStatus,
                });
                allocations.push({ sale_id: s.id, amount: apply });
                remaining -= apply;
              }
            }
            if (data.sale_id && remaining > 0.001) throw new Error('Payment exceeds selected invoice due amount');
            await db.customer_payments.update(id, { allocations });
          }
        }
      }

      return { id };
    }

    if (resource === 'supplier-payments') {
      const isRefund = data.payment_type === 'refund_received';
      const id = await db.supplier_payments.add({
        supplier_id: data.supplier_id,
        payment_date: data.payment_date || new Date().toISOString().slice(0, 10),
        payment_method: data.payment_method || 'cash',
        payment_type: data.payment_type || 'paid',
        amount: parseFloat(data.amount) || 0,
        notes: data.notes || '',
        user_id: data.user_id || 1,
        created_at: new Date().toISOString(),
      });

      if (data.supplier_id && data.amount) {
        const supplier = await db.suppliers.get(data.supplier_id);
        if (supplier) {
          const currentBal = Number(supplier.balance || 0);
          const currentCredit = Number(supplier.credit_balance || 0);
          const amount = parseFloat(data.amount) || 0;
          if (isRefund) {
            const newCredit = Math.max(0, currentCredit - amount);
            let newBal = currentBal;
            if (amount > currentCredit) {
              newBal += (amount - currentCredit);
            }
            await db.suppliers.update(data.supplier_id, { balance: newBal, credit_balance: newCredit });
          } else {
            let newBal = currentBal;
            let newCredit = currentCredit;
            if (data.is_advance || currentBal <= 0) {
              newCredit += amount;
            } else {
              if (amount <= currentBal) {
                newBal -= amount;
              } else {
                newCredit += (amount - currentBal);
                newBal = 0;
              }
            }
            await db.suppliers.update(data.supplier_id, { balance: newBal, credit_balance: newCredit });
          }
        }

        if (!isRefund && !data.is_advance) {
          // An advance remains supplier credit and must not settle a purchase.
          // Regular supplier payments are allocated FIFO to purchase dues.
          let remaining = parseFloat(data.amount) || 0;
          if (remaining > 0) {
            const supplierPurchases = await db.purchases.where('supplier_id').equals(data.supplier_id).toArray();
            supplierPurchases.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
            for (const p of supplierPurchases) {
              if (remaining <= 0) break;
              const purchaseTotal = parseFloat(p.total) || 0;
              const purchasePaid = parseFloat(p.paid_amount) || 0;
              const due = Math.max(0, purchaseTotal - purchasePaid);
              if (due > 0) {
                const apply = Math.min(due, remaining);
                const newPaid = purchasePaid + apply;
                const newStatus = newPaid >= purchaseTotal - 0.001 ? 'paid' : 'partial';
                await db.purchases.update(p.id, {
                  paid_amount: newPaid,
                  payment_status: newStatus,
                });
                remaining -= apply;
              }
            }
          }
        }
      }

      return { id };
    }

    if (resource === 'expenses') {
      const id = await db.expenses.add({
        ...data,
        created_at: new Date().toISOString(),
      });
      return { id };
    }

    if (resource === 'expense-types') {
      const id = await db.expense_types.add({
        ...data,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      return { id };
    }

    if (resource === 'staff') {
      const id = await db.staff.add({
        ...data,
        created_at: new Date().toISOString(),
      });
      return { id };
    }

    if (resource === 'staff-transactions') {
      const staffId = Number(data.staff_id || 0);
      const type = (data.type || 'salary').trim();
      const amount = Number(data.amount || 0);
      let deductionAmount = Number(data.deduction_amount || 0);
      let netPaid = Number(data.net_paid || amount);

      const staffMember = db.staff ? await db.staff.get(staffId) : null;
      let currentAdvance = Number(staffMember?.advanced_payment || 0);
      let newAdvance = currentAdvance;

      if (type === 'salary') {
        newAdvance = Math.max(0, currentAdvance - deductionAmount);
        netPaid = Math.max(0, amount - deductionAmount);
      } else if (type === 'advance') {
        netPaid = amount;
        deductionAmount = 0;
        newAdvance = currentAdvance + amount;
      } else if (type === 'deduction') {
        netPaid = 0;
        deductionAmount = amount;
        newAdvance = Math.max(0, currentAdvance - amount);
      } else if (type === 'adjustment') {
        netPaid = 0;
        deductionAmount = 0;
        newAdvance = Math.max(0, amount);
      }

      if (db.staff && staffId) {
        await db.staff.update(staffId, { advanced_payment: newAdvance, updated_at: new Date().toISOString() });
      }

      const txRecord = {
        staff_id: staffId,
        type,
        amount,
        deduction_amount: deductionAmount,
        net_paid: netPaid,
        payment_method: (data.payment_method || 'cash').toLowerCase(),
        transaction_date: data.transaction_date || new Date().toISOString().slice(0, 10),
        notes: (data.notes || '').trim(),
        expense_id: data.expense_id || null,
        user_id: data.user_id || 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      let id = null;
      if (db.staff_transactions) {
        id = await db.staff_transactions.add(txRecord);
      }

      return {
        id,
        transaction: { id, ...txRecord },
        staff: {
          id: staffId,
          advanced_payment: newAdvance,
        },
        message: 'Transaction recorded successfully (Saved offline)',
      };
    }

    if (resource === 'users') {
      const id = await db.users.add({
        ...data,
        is_active: 1,
        created_at: new Date().toISOString(),
      });
      return { id };
    }

    if (resource === 'cash-transactions') {
      const authUser = getCurrentAuthUser();
      const currentUserId = Number(authUser?.id || 1);
      const type = (data.type || 'deposit').toLowerCase();
      const amount = Number(data.amount || 0);
      const count = db.cash_transactions ? await db.cash_transactions.count() : 0;
      const prefix = type === 'deposit' ? 'DEP' : 'WTH';
      const reference = data.reference || `${prefix}-${String(count + 1).padStart(4, '0')}`;
      const record = {
        type,
        account_name: data.account_name || 'Owner Personal Account (Cash)',
        amount,
        payment_method: (data.payment_method || 'cash').toLowerCase(),
        category: data.category || (type === 'deposit' ? 'Owner Capital' : 'Owner Drawing'),
        reason: data.category || data.reason || (type === 'deposit' ? 'Owner Capital' : 'Owner Drawing'),
        reference,
        notes: (data.notes || '').trim() || null,
        transaction_date: data.transaction_date || new Date().toISOString().slice(0, 10),
        user_id: currentUserId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      let id = null;
      if (db.cash_transactions) {
        id = await db.cash_transactions.add(record);
      }
      return {
        id,
        transaction: { id, ...record },
        message: `Cash ${type === 'deposit' ? 'Deposit' : 'Withdrawal'} of ${amount} recorded successfully (Saved offline)`,
      };
    }

    if (resource === 'daily-closings' && parts[1] === 'close') {
      const authUser = getCurrentAuthUser();
      const currentUserId = Number(authUser?.id || 1);
      const userRole = (authUser?.role || 'cashier').toLowerCase();
      const isAdmin = userRole === 'admin';

      const targetDate = (data.business_date || new Date().toISOString().slice(0, 10)).trim();
      const targetStaffId = (isAdmin && data.staff_id) ? Number(data.staff_id) : currentUserId;

      // Get target user details
      const users = db.users ? await db.users.toArray() : [];
      const targetUser = users.find((u) => Number(u.id) === targetStaffId) || (targetStaffId === currentUserId ? authUser : null);
      const staffName = targetUser?.full_name || targetUser?.username || (targetStaffId === 1 ? 'Shop Owner' : 'Staff');
      const staffRole = targetUser?.role || (targetStaffId === 1 ? 'admin' : 'cashier');

      // Calculate shift totals from local sales
      const allSales = await db.sales.toArray();
      const shiftSales = allSales.filter((s) => {
        const sDate = (s.created_at || '').slice(0, 10);
        return sDate === targetDate && Number(s.user_id || 1) === targetStaffId;
      });

      let invoiceCount = shiftSales.length;
      let totalSales = 0;
      let cashSales = 0;
      let cardSales = 0;
      let onlineSales = 0;
      let creditSales = 0;

      shiftSales.forEach((s) => {
        const tot = Number(s.total || 0);
        const paid = Math.min(tot, Math.max(0, Number(s.paid_amount || 0)));
        const creditDue = Math.max(0, tot - paid);
        totalSales += tot;
        creditSales += creditDue;

        const method = String(s.payment_method || 'cash').toLowerCase();
        if (method === 'card') cardSales += paid;
        else if (method === 'online' || method === 'bank') onlineSales += paid;
        else cashSales += paid;
      });

      const closingRecord = {
        business_date: targetDate,
        staff_id: targetStaffId,
        staff_name: staffName,
        role: staffRole,
        invoice_count: invoiceCount,
        total_sales: totalSales,
        cash_sales: cashSales,
        card_sales: cardSales,
        online_sales: onlineSales,
        credit_sales: creditSales,
        closed_by: currentUserId,
        closed_at: new Date().toISOString(),
        status: 'CLOSED',
        notes: (data.notes || '').trim() || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Check if existing record in Dexie
      let existing = null;
      if (db.daily_closings) {
        existing = await db.daily_closings
          .where('[business_date+staff_id]')
          .equals([targetDate, targetStaffId])
          .first();
      }

      let closingId;
      if (existing && existing.id) {
        await db.daily_closings.update(existing.id, closingRecord);
        closingId = existing.id;
      } else if (db.daily_closings) {
        closingId = await db.daily_closings.add(closingRecord);
      }

      return {
        id: closingId,
        message: 'Sales closed successfully for today (Saved offline)',
        closing_id: closingId,
        status: 'CLOSED',
        data: closingRecord,
      };
    }

    throw new Error(`POST ${endpoint} not supported in offline mode`);
  },

  put: async (endpoint, data) => {
    const { parts, id } = parseEndpoint(endpoint);
    const resource = parts[0];

    if (resource === 'settings') {
      saveSettings(data);
      return data;
    }

    if (resource === 'medicines' && id) {
      await db.medicines.update(id, data);
      return data;
    }

    if (resource === 'categories' && id) {
      await db.categories.update(id, data);
      return data;
    }

    if (resource === 'customers' && id) {
      if (parts[2] === 'clear-balance') {
        await db.customers.update(id, { balance: 0 });
        return { message: 'Balance cleared' };
      }
      await db.customers.update(id, data);
      return data;
    }

    if (resource === 'suppliers' && id) {
      if (parts[2] === 'clear-balance') {
        await db.suppliers.update(id, { balance: 0, credit_balance: 0 });
        return { message: 'Balance cleared' };
      }
      await db.suppliers.update(id, data);
      return data;
    }

    if (resource === 'expenses' && id) {
      await db.expenses.update(id, data);
      return data;
    }

    if (resource === 'expense-types' && id) {
      await db.expense_types.update(id, data);
      return data;
    }

    if (resource === 'staff' && id) {
      await db.staff.update(id, data);
      return data;
    }

    if (resource === 'staff-transactions' && id) {
      if (db.staff_transactions) {
        await db.staff_transactions.update(id, {
          ...data,
          updated_at: new Date().toISOString(),
        });
      }
      return { id, ...data };
    }

    if (resource === 'users' && id) {
      await db.users.update(id, data);
      return data;
    }

    if (resource === 'cash-transactions' && id) {
      if (db.cash_transactions) {
        await db.cash_transactions.update(id, {
          ...data,
          updated_at: new Date().toISOString(),
        });
      }
      return { id, ...data };
    }

    if (resource === 'sales' && id) {
      const sale = await db.sales.get(id);
      if (!sale) throw new Error('Sale not found');
      const updatedFields = {};
      if (data.subtotal !== undefined) updatedFields.subtotal = data.subtotal;
      if (data.discount !== undefined) updatedFields.discount = data.discount;
      if (data.tax !== undefined) updatedFields.tax = data.tax;
      if (data.total !== undefined) updatedFields.total = data.total;
      if (data.payment_method !== undefined) updatedFields.payment_method = data.payment_method;
      if (data.payment_status !== undefined) updatedFields.payment_status = data.payment_status;
      if (data.paid_amount !== undefined) updatedFields.paid_amount = data.paid_amount;
      if (data.notes !== undefined) updatedFields.notes = data.notes;
      if (Object.keys(updatedFields).length > 0) {
        await db.sales.update(id, updatedFields);
      }
      if (data.items) {
        const oldItems = await db.sale_items.where('sale_id').equals(id).toArray();
        const oldItemsMap = {};
        oldItems.forEach(i => { oldItemsMap[i.id] = i; });
        for (const item of data.items) {
          if (item._isDeleting) {
            if (item.id) {
              const originalQty = oldItemsMap[item.id]?.quantity || item.quantity;
              const origBatchId = oldItemsMap[item.id]?.batch_id;
              if (origBatchId) {
                const batch = await db.batches.get(origBatchId);
                if (batch) {
                  await db.batches.update(origBatchId, { quantity: batch.quantity + originalQty });
                }
              }
              await db.sale_items.delete(item.id);
            }
          } else if (item.id && !item.isNew) {
            const oldItem = oldItemsMap[item.id];
            if (oldItem) {
              const qtyDiff = item.quantity - oldItem.quantity;
              if (qtyDiff !== 0 && oldItem.batch_id) {
                const batch = await db.batches.get(oldItem.batch_id);
                if (batch) {
                  await db.batches.update(oldItem.batch_id, { quantity: batch.quantity - qtyDiff });
                }
              }
              await db.sale_items.update(item.id, {
                quantity: item.quantity,
                unit_price: item.unit_price,
                discount: item.discount || 0,
                total: item.total,
              });
            }
          } else if (item.isNew) {
            await db.sale_items.add({
              sale_id: id,
              medicine_id: item.medicine_id,
              batch_id: item.batch_id,
              quantity: item.quantity,
              unit_price: item.unit_price,
              discount: item.discount || 0,
              total: item.total,
              medicine_name: item.medicine_name || '',
            });
          }
        }
      }
      return data;
    }

    if (resource === 'supplier-refunds' && id) {
      const existing = await db.supplier_refunds.get(id);
      if (!existing) throw new Error('Supplier refund not found');
      const oldAmount = parseFloat(existing.refund_amount) || 0;
      if (oldAmount > 0 && existing.supplier_id) {
        const supplier = await db.suppliers.get(existing.supplier_id);
        if (supplier) {
          await db.suppliers.update(existing.supplier_id, { balance: Number(supplier.balance || 0) + oldAmount });
        }
      }

      const oldItems = db.supplier_refund_items ? await db.supplier_refund_items.where('supplier_refund_id').equals(id).toArray() : [];
      if (existing.refund_type === 'return') {
        for (const it of oldItems) {
          if (it.medicine_id && it.return_quantity) {
            const batches = await db.batches.where('medicine_id').equals(it.medicine_id).toArray();
            if (batches.length > 0) {
              await db.batches.update(batches[0].id, { quantity: (Number(batches[0].quantity) || 0) + it.return_quantity });
            }
          }
        }
      }

      const rawItems = Array.isArray(data.items) ? data.items : [];
      let newTotal = parseFloat(data.refund_amount) || 0;
      if (newTotal <= 0 && rawItems.length > 0) {
        newTotal = rawItems.reduce((s, it) => s + (parseFloat(it.refund_amount) || 0), 0);
      }

      await db.supplier_refunds.update(id, {
        refund_date: data.refund_date || existing.refund_date,
        reason: data.reason !== undefined ? data.reason : existing.reason,
        notes: data.notes !== undefined ? data.notes : existing.notes,
        refund_amount: newTotal,
      });

      if (db.supplier_refund_items) {
        await db.supplier_refund_items.where('supplier_refund_id').equals(id).delete();
        for (const it of rawItems) {
          const mId = parseInt(it.medicine_id);
          const uQty = parseInt(it.unit_quantity) || 1;
          const conv = parseInt(it.conversion_factor) || 1;
          const retQty = it.return_quantity !== undefined ? parseInt(it.return_quantity) : (uQty * conv);
          const uPrice = parseFloat(it.unit_price) || 0;
          const rAmt = parseFloat(it.refund_amount) || (uQty * uPrice);

          await db.supplier_refund_items.add({
            supplier_refund_id: id,
            medicine_id: mId,
            unit_name: it.unit_name || null,
            unit_quantity: uQty,
            conversion_factor: conv,
            return_quantity: retQty,
            unit_price: uPrice,
            refund_amount: rAmt,
            created_at: new Date().toISOString(),
          });

          if (existing.refund_type === 'return' && mId && retQty > 0) {
            const batches = await db.batches.where('medicine_id').equals(mId).toArray();
            const sorted = batches.sort((a, b) => (a.expiry_date || '').localeCompare(b.expiry_date || ''));
            let remaining = retQty;
            for (const batch of sorted) {
              if (remaining <= 0) break;
              const deduct = Math.min(batch.quantity, remaining);
              await db.batches.update(batch.id, { quantity: batch.quantity - deduct });
              remaining -= deduct;
            }
          }
        }
      }

      if (existing.supplier_id && newTotal > 0) {
        const supplier = await db.suppliers.get(existing.supplier_id);
        if (supplier) {
          await db.suppliers.update(existing.supplier_id, { balance: Number(supplier.balance || 0) - newTotal });
        }
      }

      return { message: 'Supplier refund updated successfully' };
    }

    if (resource === 'customer-refunds' && id) {
      const existing = await db.customer_refunds.get(id);
      if (!existing) throw new Error('Customer refund not found');
      const oldAmount = parseFloat(existing.refund_amount) || 0;
      if (oldAmount > 0 && existing.customer_id) {
        const customer = await db.customers.get(existing.customer_id);
        if (customer) {
          await db.customers.update(existing.customer_id, { balance: Number(customer.balance || 0) + oldAmount });
        }
      }

      const oldItems = db.customer_refund_items ? await db.customer_refund_items.where('customer_refund_id').equals(id).toArray() : [];
      if (existing.refund_type === 'return') {
        for (const it of oldItems) {
          if (it.medicine_id && it.return_quantity) {
            const batches = await db.batches.where('medicine_id').equals(it.medicine_id).toArray();
            const sorted = batches.sort((a, b) => (a.expiry_date || '').localeCompare(b.expiry_date || ''));
            let remaining = it.return_quantity;
            for (const batch of sorted) {
              if (remaining <= 0) break;
              const deduct = Math.min(batch.quantity, remaining);
              await db.batches.update(batch.id, { quantity: Math.max(0, batch.quantity - deduct) });
              remaining -= deduct;
            }
          }
        }
      }

      const rawItems = Array.isArray(data.items) ? data.items : [];
      let newTotal = parseFloat(data.refund_amount) || 0;
      if (newTotal <= 0 && rawItems.length > 0) {
        newTotal = rawItems.reduce((s, it) => s + (parseFloat(it.refund_amount) || 0), 0);
      }

      await db.customer_refunds.update(id, {
        refund_date: data.refund_date || existing.refund_date,
        reason: data.reason !== undefined ? data.reason : existing.reason,
        notes: data.notes !== undefined ? data.notes : existing.notes,
        refund_amount: newTotal,
      });

      if (db.customer_refund_items) {
        await db.customer_refund_items.where('customer_refund_id').equals(id).delete();
        for (const it of rawItems) {
          const mId = parseInt(it.medicine_id);
          const uQty = parseInt(it.unit_quantity) || 1;
          const conv = parseInt(it.conversion_factor) || 1;
          const retQty = it.return_quantity !== undefined ? parseInt(it.return_quantity) : (uQty * conv);
          const uPrice = parseFloat(it.unit_price) || 0;
          const rAmt = parseFloat(it.refund_amount) || (uQty * uPrice);

          await db.customer_refund_items.add({
            customer_refund_id: id,
            medicine_id: mId,
            unit_name: it.unit_name || null,
            unit_quantity: uQty,
            conversion_factor: conv,
            return_quantity: retQty,
            unit_price: uPrice,
            refund_amount: rAmt,
            created_at: new Date().toISOString(),
          });

          if (existing.refund_type === 'return' && mId && retQty > 0) {
            const batches = await db.batches.where('medicine_id').equals(mId).toArray();
            const sorted = batches.sort((a, b) => (b.id || 0) - (a.id || 0));
            if (sorted.length > 0) {
              await db.batches.update(sorted[0].id, { quantity: sorted[0].quantity + retQty });
            }
          }
        }
      }

      if (existing.customer_id && newTotal > 0) {
        const customer = await db.customers.get(existing.customer_id);
        if (customer) {
          await db.customers.update(existing.customer_id, { balance: Number(customer.balance || 0) - newTotal });
        }
      }

      return { message: 'Customer refund updated successfully' };
    }

    if (resource === 'purchases' && id) {
      const existing = await db.purchases.get(id);
      if (!existing) throw new Error('Purchase not found');

      // 1. Revert old batch stock
      const oldItems = await db.purchase_items.where('purchase_id').equals(id).toArray();
      for (const oi of oldItems) {
        const oldQty = (parseInt(oi.quantity) || ((parseInt(oi.unit_quantity) || 0) * (parseInt(oi.conversion_factor) || 1))) || 0;
        if (oi.medicine_id && oi.batch_number && oldQty > 0) {
          const batches = await db.batches.where('medicine_id').equals(oi.medicine_id).toArray();
          const match = batches.find((b) => b.batch_number === oi.batch_number);
          if (match) {
            await db.batches.update(match.id, { quantity: Math.max(0, (Number(match.quantity) || 0) - oldQty) });
          }
        }
      }

      // 2. Revert old supplier balance
      if (existing.supplier_id && (Number(existing.balance_due) || 0) > 0) {
        const oldSup = await db.suppliers.get(existing.supplier_id);
        if (oldSup) {
          await db.suppliers.update(existing.supplier_id, {
            balance: Math.max(0, (Number(oldSup.balance) || 0) - Number(existing.balance_due)),
          });
        }
      }

      // 3. Update purchase fields
      const updatedFields = {
        reference_number: data.reference_number || existing.reference_number,
        supplier_id: data.supplier_id !== undefined ? (data.supplier_id ? parseInt(data.supplier_id) : null) : existing.supplier_id,
        user_id: data.user_id !== undefined ? (data.user_id ? parseInt(data.user_id) : 1) : existing.user_id,
        purchase_date: data.purchase_date || existing.purchase_date,
        payment_method: data.payment_method || existing.payment_method,
        subtotal: data.subtotal !== undefined ? Number(data.subtotal) : existing.subtotal,
        discount: data.discount !== undefined ? Number(data.discount) : existing.discount,
        tax: data.tax !== undefined ? Number(data.tax) : existing.tax,
        total: data.total !== undefined ? Number(data.total) : existing.total,
        payment_status: data.payment_status || existing.payment_status,
        paid_amount: data.paid_amount !== undefined ? Number(data.paid_amount) : existing.paid_amount,
        balance_due: data.balance_due !== undefined ? Number(data.balance_due) : existing.balance_due,
        notes: data.notes !== undefined ? data.notes : existing.notes,
      };
      await db.purchases.update(id, updatedFields);

      // 4. If items provided, re-create items and restock batches
      if (Array.isArray(data.items) && data.items.length > 0) {
        await db.purchase_items.where('purchase_id').equals(id).delete();
        for (const item of data.items) {
          const uQty = parseInt(item.unit_quantity || item.quantity) || 1;
          const conv = parseInt(item.conversion_factor) || 1;
          const baseQty = uQty * conv;
          const buyPrice = parseFloat(item.buy_price || item.unit_price) || 0;
          const sellPrice = parseFloat(item.sell_price) || 0;
          const itemDisc = parseFloat(item.discount) || 0;
          const lineGross = buyPrice * uQty;
          const discAmt = (lineGross * itemDisc) / 100;
          const lineTot = item.total !== undefined ? Number(item.total) : (lineGross - discAmt);
          const bNo = item.batch_number || `BN-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${item.medicine_id}`;

          await db.purchase_items.add({
            purchase_id: id,
            medicine_id: parseInt(item.medicine_id),
            batch_number: bNo,
            mf_date: item.mf_date || null,
            expiry_date: item.expiry_date || null,
            unit_name: item.unit_name || null,
            unit_quantity: uQty,
            conversion_factor: conv,
            quantity: baseQty,
            buy_price: buyPrice,
            sell_price: sellPrice,
            discount: itemDisc,
            total: lineTot,
          });

          const existingBatches = await db.batches.where('medicine_id').equals(parseInt(item.medicine_id)).toArray();
          const matchBatch = existingBatches.find((b) => b.batch_number === bNo);
          if (matchBatch) {
            await db.batches.update(matchBatch.id, {
              quantity: (Number(matchBatch.quantity) || 0) + baseQty,
              cost_price: buyPrice > 0 ? (buyPrice / conv) : matchBatch.cost_price,
              selling_price: sellPrice > 0 ? (sellPrice / conv) : matchBatch.selling_price,
              mf_date: item.mf_date || matchBatch.mf_date,
              expiry_date: item.expiry_date || matchBatch.expiry_date,
            });
          } else {
            await db.batches.add({
              medicine_id: parseInt(item.medicine_id),
              batch_number: bNo,
              quantity: baseQty,
              cost_price: buyPrice > 0 ? (buyPrice / conv) : 0,
              selling_price: sellPrice > 0 ? (sellPrice / conv) : 0,
              mf_date: item.mf_date || null,
              expiry_date: item.expiry_date || null,
              created_at: new Date().toISOString(),
            });
          }
        }
      }

      // 5. Apply new supplier balance
      const newSupId = updatedFields.supplier_id;
      const newBalDue = Number(updatedFields.balance_due) || 0;
      if (newSupId && newBalDue > 0) {
        const sup = await db.suppliers.get(newSupId);
        if (sup) {
          await db.suppliers.update(newSupId, {
            balance: (Number(sup.balance) || 0) + newBalDue,
          });
        }
      }

      return { message: 'Purchase updated successfully', purchase_id: id };
    }

    if (resource === 'formulations' && id) {
      await db.formulations.update(id, data);
      return data;
    }

    if (resource === 'batches' && id) {
      await db.batches.update(id, data);
      return data;
    }

    if (resource === 'damages' && id) {
      await db.damages.update(id, data);
      return data;
    }

    throw new Error(`PUT ${endpoint} not supported in offline mode`);
  },

  patch: async (endpoint, data = {}) => {
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 401) throw handleUnauthorizedResponse();
      throw new Error(json?.message || `Request failed with status ${res.status}`);
    }
    return json?.data !== undefined ? json.data : json;
  },

  delete: async (endpoint) => {
    const { parts, id } = parseEndpoint(endpoint);
    const resource = parts[0];

    if (resource === 'medicines' && id) {
      await db.medicines.delete(id);
      await db.batches.where('medicine_id').equals(id).delete();
      return { message: 'Deleted' };
    }

    if (resource === 'categories' && id) {
      await db.categories.delete(id);
      return { message: 'Deleted' };
    }

    if (resource === 'customers' && id) {
      await db.customers.delete(id);
      return { message: 'Deleted' };
    }

    if (resource === 'suppliers' && id) {
      await db.suppliers.delete(id);
      return { message: 'Deleted' };
    }

    if (resource === 'expenses' && id) {
      await db.expenses.delete(id);
      return { message: 'Deleted' };
    }

    if (resource === 'staff' && id) {
      await db.staff.delete(id);
      return { message: 'Deleted' };
    }

    if (resource === 'staff-transactions' && id) {
      if (db.staff_transactions) {
        await db.staff_transactions.delete(id);
      }
      return { message: 'Deleted' };
    }

    if (resource === 'users' && id) {
      await db.users.delete(id);
      return { message: 'Deleted' };
    }

    if (resource === 'cash-transactions' && id) {
      if (db.cash_transactions) {
        await db.cash_transactions.delete(id);
      }
      return { message: 'Deleted' };
    }

    if (resource === 'customer-payments' && id) {
      const payment = await db.customer_payments.get(id);
      if (payment && payment.customer_id) {
        const customer = await db.customers.get(payment.customer_id);
        if (customer) {
          const currentBal = Number(customer.balance || 0);
          const newBal = currentBal + (parseFloat(payment.amount) || 0);
          await db.customers.update(payment.customer_id, { balance: newBal });
        }
        if (payment.payment_type !== 'refund') {
          const allocations = payment.allocations || (payment.sale_id ? [{ sale_id: payment.sale_id, amount: payment.amount }] : []);
          if (!allocations.length) throw new Error('Legacy payment cannot be deleted offline because its invoice allocation is unknown');
          for (const allocation of allocations) {
            const sale = await db.sales.get(allocation.sale_id);
            if (!sale) continue;
            const paid = Math.max(0, Number(sale.paid_amount || 0) - Number(allocation.amount || 0));
            await db.sales.update(sale.id, { paid_amount: paid, payment_status: paid >= Number(sale.total) - 0.001 ? 'paid' : paid > 0 ? 'partial' : 'unpaid' });
          }
        }
      }
      await db.customer_payments.delete(id);
      return { message: 'Deleted' };
    }

    if (resource === 'customer-refunds' && id) {
      const refund = await db.customer_refunds.get(id);
      if (refund && refund.customer_id) {
        const customer = await db.customers.get(refund.customer_id);
        if (customer) {
          const currentBal = Number(customer.balance || 0);
          const newBal = currentBal + (parseFloat(refund.refund_amount) || 0);
          await db.customers.update(refund.customer_id, { balance: newBal });
        }
      }
      const rawItems = db.customer_refund_items ? await db.customer_refund_items.where('customer_refund_id').equals(id).toArray() : [];
      if (refund && refund.refund_type === 'return') {
        const itemsToRevert = rawItems.length > 0 ? rawItems : (refund.medicine_id ? [{ medicine_id: refund.medicine_id, return_quantity: refund.return_quantity }] : []);
        for (const it of itemsToRevert) {
          if (it.medicine_id && it.return_quantity) {
            const batches = await db.batches.where('medicine_id').equals(it.medicine_id).toArray();
            const sorted = batches.sort((a, b) => (a.expiry_date || '').localeCompare(b.expiry_date || ''));
            let remaining = parseInt(it.return_quantity) || 0;
            for (const batch of sorted) {
              if (remaining <= 0) break;
              const deduct = Math.min(batch.quantity, remaining);
              await db.batches.update(batch.id, { quantity: Math.max(0, batch.quantity - deduct) });
              remaining -= deduct;
            }
          }
        }
      }
      if (db.customer_refund_items) {
        await db.customer_refund_items.where('customer_refund_id').equals(id).delete();
      }
      await db.customer_refunds.delete(id);
      return { message: 'Deleted' };
    }

    if (resource === 'supplier-refunds' && id) {
      const refund = await db.supplier_refunds.get(id);
      if (refund && refund.supplier_id) {
        const supplier = await db.suppliers.get(refund.supplier_id);
        if (supplier) {
          const currentBal = Number(supplier.balance || 0);
          const newBal = currentBal + (parseFloat(refund.refund_amount) || 0);
          await db.suppliers.update(refund.supplier_id, { balance: newBal });
        }
      }
      const rawItems = db.supplier_refund_items ? await db.supplier_refund_items.where('supplier_refund_id').equals(id).toArray() : [];
      if (refund && refund.refund_type === 'return') {
        const itemsToRevert = rawItems.length > 0 ? rawItems : (refund.medicine_id ? [{ medicine_id: refund.medicine_id, return_quantity: refund.return_quantity }] : []);
        for (const it of itemsToRevert) {
          if (it.medicine_id && it.return_quantity) {
            const batches = await db.batches.where('medicine_id').equals(it.medicine_id).toArray();
            if (batches.length > 0) {
              await db.batches.update(batches[0].id, { quantity: (Number(batches[0].quantity) || 0) + parseInt(it.return_quantity) });
            }
          }
        }
      }
      if (db.supplier_refund_items) {
        await db.supplier_refund_items.where('supplier_refund_id').equals(id).delete();
      }
      await db.supplier_refunds.delete(id);
      return { message: 'Deleted' };
    }

    if (resource === 'supplier-payments' && id) {
      const payment = await db.supplier_payments.get(id);
      if (payment && payment.supplier_id) {
        const supplier = await db.suppliers.get(payment.supplier_id);
        if (supplier) {
          const currentBal = Number(supplier.balance || 0);
          const currentCredit = Number(supplier.credit_balance || 0);
          const amount = parseFloat(payment.amount) || 0;
          if (payment.payment_type === 'refund_received') {
            await db.suppliers.update(payment.supplier_id, { credit_balance: currentCredit + amount });
          } else {
            await db.suppliers.update(payment.supplier_id, { balance: currentBal + amount });
          }
        }
      }
      await db.supplier_payments.delete(id);
      return { message: 'Deleted' };
    }

    if (resource === 'sales' && id) {
      const sale = await db.sales.get(id);
      if (sale) {
        if (sale.customer_id) {
          const owing = Math.max(0, (Number(sale.total) || 0) - (Number(sale.paid_amount) || 0));
          if (owing > 0) {
            const customer = await db.customers.get(sale.customer_id);
            if (customer) {
              const currentBal = Number(customer.balance || 0);
              const newBal = Math.max(0, currentBal - owing);
              await db.customers.update(sale.customer_id, { balance: newBal });
            }
          }
        }
        const items = await db.sale_items.where('sale_id').equals(id).toArray();
        for (const item of items) {
          if (item.batch_id && item.quantity) {
            const batch = await db.batches.get(item.batch_id);
            if (batch) {
              await db.batches.update(item.batch_id, {
                quantity: (Number(batch.quantity) || 0) + (Number(item.quantity) || 0),
              });
            }
          }
        }
        await db.sale_items.where('sale_id').equals(id).delete();
        await db.sales.delete(id);
      }
      return { message: 'Sale deleted successfully' };
    }

    if (resource === 'purchases' && id) {
      const purchase = await db.purchases.get(id);
      if (purchase) {
        if (purchase.supplier_id) {
          const balanceDue = Number(purchase.balance_due) || 0;
          if (balanceDue > 0) {
            const supplier = await db.suppliers.get(purchase.supplier_id);
            if (supplier) {
              const currentBal = Number(supplier.balance || 0);
              const newBal = Math.max(0, currentBal - balanceDue);
              await db.suppliers.update(purchase.supplier_id, { balance: newBal });
            }
          }
        }
        const items = await db.purchase_items.where('purchase_id').equals(id).toArray();
        for (const item of items) {
          const baseQty = (parseInt(item.quantity) || ((parseInt(item.unit_quantity) || 0) * (parseInt(item.conversion_factor) || 1))) || 0;
          if (item.medicine_id && item.batch_number && baseQty > 0) {
            const batches = await db.batches
              .where('medicine_id')
              .equals(item.medicine_id)
              .toArray();
            const matchingBatch = batches.find((b) => b.batch_number === item.batch_number);
            if (matchingBatch) {
              const newQty = Math.max(0, (Number(matchingBatch.quantity) || 0) - baseQty);
              await db.batches.update(matchingBatch.id, { quantity: newQty });
            }
          }
        }
        await db.purchase_items.where('purchase_id').equals(id).delete();
        await db.purchases.delete(id);
      }
      return { message: 'Purchase deleted successfully' };
    }

    if (resource === 'damages' && id) {
      const damage = await db.damages.get(id);
      if (damage) {
        if (damage.batch_id && damage.quantity) {
          const batch = await db.batches.get(damage.batch_id);
          if (batch) {
            await db.batches.update(damage.batch_id, {
              quantity: (Number(batch.quantity) || 0) + (Number(damage.quantity) || 0),
            });
          }
        }
        await db.damages.delete(id);
      }
      return { message: 'Deleted' };
    }

    if (resource === 'expense-types' && id) {
      await db.expense_types.delete(id);
      return { message: 'Deleted' };
    }

    if (resource === 'formulations' && id) {
      await db.formulations.delete(id);
      return { message: 'Deleted' };
    }

    if (resource === 'batches' && id) {
      await db.batches.delete(id);
      return { message: 'Deleted' };
    }

    throw new Error(`DELETE ${endpoint} not supported in offline mode`);
  },
};

// Mirror server data into local IndexedDB
async function mirrorServerGetToLocal(endpoint, data) {
  try {
    const { parts, id } = parseEndpoint(endpoint);
    const resource = parts[0];

    if (resource === 'categories' && Array.isArray(data)) {
      await db.categories.bulkPut(data);
    } else if (resource === 'formulations') {
      const list = Array.isArray(data) ? data : (data?.formulations || []);
      if (list.length > 0) await db.formulations.bulkPut(list);
    } else if (resource === 'customers') {
      if (id && data && typeof data === 'object') {
        await db.customers.put(data);
      } else {
        const list = Array.isArray(data) ? data : (data?.customers || []);
        if (list.length > 0) await db.customers.bulkPut(list);
      }
    } else if (resource === 'suppliers') {
      if (id && data && typeof data === 'object' && !Array.isArray(data)) {
        await db.suppliers.put(data);
      } else {
        const list = Array.isArray(data) ? data : (data?.suppliers || []);
        if (list.length > 0) await db.suppliers.bulkPut(list);
      }
    } else if (resource === 'medicines') {
      if (id && data && typeof data === 'object') {
        const { batches, ...rest } = data;
        await db.medicines.put(rest);
        if (Array.isArray(batches) && batches.length > 0) {
          await db.batches.bulkPut(batches);
        }
      } else {
        const list = Array.isArray(data) ? data : (data?.medicines || []);
        if (list.length > 0) {
          const cleanMeds = list.map((m) => {
            const { batches, ...rest } = m;
            return rest;
          });
          await db.medicines.bulkPut(cleanMeds);
          const allBatches = list.flatMap((m) => m.batches || []);
          if (allBatches.length > 0) await db.batches.bulkPut(allBatches);
        }
      }
    } else if (resource === 'batches') {
      if (Array.isArray(data)) await db.batches.bulkPut(data);
    } else if (resource === 'sales') {
      const list = Array.isArray(data) ? data : (data?.sales || (data && id ? [data] : []));
      if (list.length > 0) await db.sales.bulkPut(list);
    } else if (resource === 'sale-items') {
      const list = Array.isArray(data) ? data : (data?.data || data?.items || []);
      if (list.length > 0) await db.sale_items.bulkPut(list);
    } else if (resource === 'purchases') {
      const list = Array.isArray(data) ? data : (data?.purchases || (data && id ? [data] : []));
      if (list.length > 0) await db.purchases.bulkPut(list);
    } else if (resource === 'customer-payments') {
      const list = Array.isArray(data) ? data : (data?.payments || []);
      if (list.length > 0) await db.customer_payments.bulkPut(list);
    } else if (resource === 'supplier-payments') {
      const list = Array.isArray(data) ? data : (data?.payments || []);
      if (list.length > 0) await db.supplier_payments.bulkPut(list);
    } else if (resource === 'customer-refunds') {
      const list = Array.isArray(data) ? data : (data?.refunds || (data && id ? [data] : []));
      if (list.length > 0) await db.customer_refunds.bulkPut(list);
    } else if (resource === 'supplier-refunds') {
      const list = Array.isArray(data) ? data : (data?.refunds || (data && id ? [data] : []));
      if (list.length > 0) await db.supplier_refunds.bulkPut(list);
    } else if (resource === 'damages') {
      const list = Array.isArray(data) ? data : (data?.damages || []);
      if (list.length > 0) await db.damages.bulkPut(list);
    } else if (resource === 'expenses') {
      const list = Array.isArray(data) ? data : (data?.expenses || []);
      if (list.length > 0) await db.expenses.bulkPut(list);
    } else if (resource === 'staff') {
      const list = Array.isArray(data) ? data : (data?.staff || []);
      if (list.length > 0) await db.staff.bulkPut(list);
    } else if (resource === 'users') {
      const list = Array.isArray(data) ? data : (data?.users || []);
      if (list.length > 0) await db.users.bulkPut(list);
    } else if (resource === 'daily-closings') {
      const list = Array.isArray(data) ? data : (data?.cashiers || []);
      if (Array.isArray(list) && list.length > 0 && db.daily_closings) {
        const closingsToSave = list.filter((c) => c.status === 'CLOSED').map((c) => ({
          business_date: data?.date || new Date().toISOString().slice(0, 10),
          staff_id: c.staff_id,
          staff_name: c.staff_name,
          role: c.role,
          invoice_count: c.invoice_count,
          total_sales: c.total_sales,
          cash_sales: c.cash_sales,
          card_sales: c.card_sales,
          online_sales: c.online_sales,
          credit_sales: c.credit_sales,
          closed_by: c.closed_by,
          closed_at: c.closed_at,
          status: 'CLOSED',
          notes: c.notes,
        }));
        if (closingsToSave.length > 0) {
          await db.daily_closings.bulkPut(closingsToSave);
        }
      }
    } else if (resource === 'cash-transactions') {
      const list = Array.isArray(data) ? data : (data?.transactions || []);
      if (Array.isArray(list) && list.length > 0 && db.cash_transactions) {
        await db.cash_transactions.bulkPut(list);
      }
    } else if (resource === 'staff-transactions') {
      const list = Array.isArray(data) ? data : (data?.transactions || data?.data || []);
      if (Array.isArray(list) && list.length > 0 && db.staff_transactions) {
        await db.staff_transactions.bulkPut(list);
      }
    } else if (resource === 'settings' && data && typeof data === 'object') {
      saveSettings(data);
    }
  } catch (err) {
    console.warn('Failed to mirror server GET to local IndexedDB:', err);
  }
}

// Mirror POST result into local IndexedDB
async function mirrorServerPostToLocal(endpoint, payload, serverResult) {
  try {
    const { parts } = parseEndpoint(endpoint);
    const resource = parts[0];

    if (resource === 'pos' && parts[1] === 'checkout') {
      await localApi.post(endpoint, payload);
    } else if (resource === 'daily-closings' && parts[1] === 'close') {
      await localApi.post(endpoint, payload);
    } else if (resource === 'customer-payments') {
      await localApi.post(endpoint, payload);
    } else if (resource === 'supplier-payments') {
      // The server has already committed this payment. Mirror that exact record
      // locally instead of re-running the offline payment flow (which could
      // create a different local record and adjust the supplier twice).
      if (serverResult?.id) {
        await db.supplier_payments.put({
          ...payload,
          ...serverResult,
          id: serverResult.id,
          created_at: serverResult.created_at || new Date().toISOString(),
        });

        if (payload.supplier_id) {
          const supplier = await db.suppliers.get(payload.supplier_id);
          if (supplier) {
            await db.suppliers.update(payload.supplier_id, {
              balance: serverResult.balance ?? supplier.balance,
              credit_balance: serverResult.credit_balance ?? supplier.credit_balance,
              updated_at: new Date().toISOString(),
            });
          }
        }
      }
    } else if (resource === 'customer-refunds') {
      await localApi.post(endpoint, payload);
    } else if (resource === 'supplier-refunds') {
      await localApi.post(endpoint, payload);
    } else if (resource === 'damages') {
      await localApi.post(endpoint, payload);
    } else if (resource === 'medicines') {
      await db.medicines.put(serverResult);
    } else if (resource === 'customers') {
      await db.customers.put(serverResult);
    } else if (resource === 'suppliers') {
      await db.suppliers.put(serverResult);
    } else if (resource === 'categories') {
      await db.categories.put(serverResult);
    } else if (resource === 'formulations') {
      await db.formulations.put(serverResult);
    } else if (resource === 'batches') {
      await db.batches.put(serverResult);
    } else if (resource === 'purchases') {
      await localApi.post(endpoint, payload);
    } else if (resource === 'expenses') {
      await db.expenses.put(serverResult);
    } else if (resource === 'staff') {
      await db.staff.put(serverResult);
    } else if (resource === 'users') {
      await db.users.put(serverResult);
    } else if (resource === 'cash-transactions') {
      const tx = serverResult?.transaction || (serverResult?.id ? serverResult : payload);
      if (tx && db.cash_transactions) {
        await db.cash_transactions.put(tx);
      }
    } else if (resource === 'staff-transactions') {
      const tx = serverResult?.transaction || (serverResult?.id ? serverResult : payload);
      if (tx && db.staff_transactions) {
        await db.staff_transactions.put(tx);
      }
      if (serverResult?.staff?.id && db.staff) {
        await db.staff.update(serverResult.staff.id, {
          advanced_payment: serverResult.staff.advanced_payment,
        });
      }
    }
  } catch (err) {
    console.warn('Failed to mirror server POST to local IndexedDB:', err);
  }
}

// Hybrid API router: online direct API + Dexie mirror & offline local storage + sync queue
const api = {
  get: async (endpoint) => {
    if (syncService.isOnline) {
      try {
        const res = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: 'GET',
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const json = await res.json();
          const data = json?.data !== undefined ? json.data : json;
          mirrorServerGetToLocal(endpoint, data);
          return data;
        } else if (res.status === 401 || res.status === 403) {
          throw handleUnauthorizedResponse();
        } else if (res.status === 404 || res.status >= 500) {
          return localApi.get(endpoint);
        }
      } catch (err) {
        if (err?.name === 'UnauthorizedError') throw err;
        console.warn('Online GET failed, falling back to local DB:', err);
        syncService.handleRequestFailure(err);
      }
    }
    return localApi.get(endpoint);
  },

  post: async (endpoint, data) => {
    const { parts } = parseEndpoint(endpoint);
    const resource = parts[0];

    // AI endpoints use local Gemini helper
    if (resource === 'ai') {
      return localApi.post(endpoint, data);
    }

    if (syncService.isOnline) {
      try {
        const res = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(data),
        });

        if (res.ok) {
          const json = await res.json();
          const serverResult = json?.data !== undefined ? json.data : json;
          await mirrorServerPostToLocal(endpoint, data, serverResult);
          notifyAlertRefresh(resource);
          return serverResult;
        } else {
          if (res.status === 401 || res.status === 403) {
            throw handleUnauthorizedResponse();
          }
          if (res.status >= 400 && res.status < 500) {
            const errJson = await res.json().catch(() => ({}));
            throw new Error(errJson?.message || `Request failed with status ${res.status}`);
          }

          // A server error is not an offline condition. Falling back to local
          // storage here produced a success toast without creating a payment on
          // the server, leaving supplier dues unchanged.
          const errJson = await res.json().catch(() => ({}));
          throw new Error(errJson?.message || `Server error (${res.status}). Please try again.`);
        }
      } catch (err) {
        if (err.message && !err.message.includes('Failed to fetch') && !err.message.includes('NetworkError')) {
          throw err;
        }
        console.warn('Online POST failed, queuing offline mutation:', err);
        syncService.handleRequestFailure(err);
      }
    }

    // Offline execution + enqueue
    const localRes = await localApi.post(endpoint, data);
    const localId = localRes?.id || localRes?.closing_id || localRes?.sale_id || null;
    await syncService.enqueue('CREATE', resource, localId, endpoint, 'POST', data);
    return localRes;
  },

  put: async (endpoint, data) => {
    const { parts, id } = parseEndpoint(endpoint);
    const resource = parts[0];

    if (syncService.isOnline) {
      try {
        const res = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(data),
        });

        if (res.ok) {
          const json = await res.json();
          const serverResult = json?.data !== undefined ? json.data : json;
          await localApi.put(endpoint, data);
          notifyAlertRefresh(resource);
          return serverResult;
        } else {
          if (res.status === 401 || res.status === 403) {
            throw handleUnauthorizedResponse();
          }
          if (res.status >= 400 && res.status < 500) {
            const errJson = await res.json().catch(() => ({}));
            throw new Error(errJson?.message || `Request failed with status ${res.status}`);
          }
        }
      } catch (err) {
        if (err.message && !err.message.includes('Failed to fetch') && !err.message.includes('NetworkError')) {
          throw err;
        }
        console.warn('Online PUT failed, queuing offline mutation:', err);
        syncService.handleRequestFailure(err);
      }
    }

    // Offline execution + enqueue
    const localRes = await localApi.put(endpoint, data);
    await syncService.enqueue('UPDATE', resource, id, endpoint, 'PUT', data);
    return localRes;
  },

  delete: async (endpoint) => {
    const { parts, id } = parseEndpoint(endpoint);
    const resource = parts[0];

    if (syncService.isOnline) {
      try {
        const res = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });

        if (res.ok) {
          const json = await res.json().catch(() => ({}));
          await localApi.delete(endpoint);
          return json?.data !== undefined ? json.data : json;
        } else {
          if (res.status === 401 || res.status === 403) {
            throw handleUnauthorizedResponse();
          }
          if (res.status >= 400 && res.status < 500) {
            const errJson = await res.json().catch(() => ({}));
            throw new Error(errJson?.message || `Request failed with status ${res.status}`);
          }
        }
      } catch (err) {
        if (err.message && !err.message.includes('Failed to fetch') && !err.message.includes('NetworkError')) {
          throw err;
        }
        console.warn('Online DELETE failed, queuing offline mutation:', err);
        syncService.handleRequestFailure(err);
      }
    }

    // Offline execution + enqueue
    const localRes = await localApi.delete(endpoint);
    await syncService.enqueue('DELETE', resource, id, endpoint, 'DELETE', null);
    return localRes;
  },

  exportBackup: async () => {
    const token = localStorage.getItem('gaza_pharmacy_token');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const res = await fetch(`${API_BASE_URL}/backup/export`, {
      method: 'GET',
      headers,
    });
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        throw handleUnauthorizedResponse();
      }
      const errJson = await res.json().catch(() => ({}));
      throw new Error(errJson?.message || `Backup export failed with status ${res.status}`);
    }
    const blob = await res.blob();
    const contentDisposition = res.headers.get('Content-Disposition') || '';
    const match = contentDisposition.match(/filename="?([^";]+)"?/i);
    const filename = match ? match[1] : `gaza_pharmacy_backup_${new Date().toISOString().slice(0, 10)}.zip`;

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return { success: true, filename };
  },

  importBackup: async (file) => {
    const token = localStorage.getItem('gaza_pharmacy_token');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const formData = new FormData();
    formData.append('backup', file);

    const res = await fetch(`${API_BASE_URL}/backup/import`, {
      method: 'POST',
      headers,
      body: formData,
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        throw handleUnauthorizedResponse();
      }
      throw new Error(json?.message || `Backup import failed with status ${res.status}`);
    }
    return json?.data !== undefined ? json.data : json;
  },

  uploadShopLogo: async (file) => {
    const token = localStorage.getItem('gaza_pharmacy_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const formData = new FormData();
    formData.append('logo', file);
    const res = await fetch(`${API_BASE_URL}/settings/logo`, { method: 'POST', headers, body: formData });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      // A 403 is a real upload/permission error, not an expired login session.
      if (res.status === 401) throw handleUnauthorizedResponse();
      throw new Error(json?.message || 'Logo upload failed');
    }
    return json?.data !== undefined ? json.data : json;
  },
};

export default api;
